import { z } from "zod";
export { createOpenApiDocument, toOpenApiPath, } from "./openapi.js";
export const requestIdSchema = z
    .string()
    .min(8)
    .max(128)
    .regex(/^[A-Za-z0-9._:-]+$/);
export const cursorSchema = z
    .string()
    .min(8)
    .max(512)
    .regex(/^[A-Za-z0-9_-]+$/);
export const cursorPageSchema = z.object({
    nextCursor: cursorSchema.nullable(),
    hasMore: z.boolean(),
    limit: z.number().int().min(10).max(100),
});
export const apiErrorCodeSchema = z.enum([
    "BAD_REQUEST",
    "UNAUTHENTICATED",
    "FORBIDDEN",
    "NOT_FOUND",
    "CONFLICT",
    "RATE_LIMITED",
    "QUOTA_EXCEEDED",
    "UNSUPPORTED_MEDIA_TYPE",
    "PAYLOAD_TOO_LARGE",
    "DEPENDENCY_UNAVAILABLE",
    "SERVICE_UNAVAILABLE",
    "INTERNAL_ERROR",
]);
export const apiErrorSchema = z.object({
    error: z.object({
        code: apiErrorCodeSchema,
        message: z.string().min(1),
        requestId: requestIdSchema,
        details: z.record(z.string(), z.unknown()).optional(),
    }),
});
export const healthResponseSchema = z.object({
    status: z.literal("ok"),
    service: z.literal("kindtutor-api"),
    environment: z.string().min(1),
    timestamp: z.iso.datetime(),
    requestId: requestIdSchema,
});
export const dependencyHealthResponseSchema = z.object({
    status: z.enum(["ok", "degraded"]),
    dependencies: z.object({
        database: z.enum(["ok", "unavailable"]),
        materials: z.enum(["ok", "unavailable"]),
    }),
    environment: z.string().min(1),
    timestamp: z.iso.datetime(),
    requestId: requestIdSchema,
});
export const publicConfigResponseSchema = z.object({
    product: z.literal("KindTutor"),
    supportEmail: z.email(),
    surfaces: z.array(z.enum(["station", "school", "hq"])),
    requestId: requestIdSchema,
});
export const hqRoleSchema = z.enum([
    "superadmin",
    "support",
    "sales",
    "operations",
    "finance",
    "read_only",
]);
export const hqPermissionSchema = z.enum([
    "dashboard.read",
    "schools.read",
    "schools.write",
    "sales.read",
    "sales.write",
    "pilots.read",
    "pilots.write",
    "fleet.read",
    "fleet.write",
    "support.read",
    "support.write",
    "billing.read",
    "billing.write",
    "settings.read",
    "settings.write",
    "audit.read",
    "exports.read",
    "impersonation.write",
]);
export const hqImpersonationSchema = z.object({
    id: z.string().min(1).max(128),
    schoolId: z.string().min(1).max(64),
    schoolName: z.string().min(1).max(240),
    actorId: z.string().min(1).max(128),
    actorName: z.string().min(1).max(200),
    reason: z.string().min(10).max(500),
    startedAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
});
export const hqImpersonationStartRequestSchema = z.object({
    schoolId: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[A-Za-z0-9_-]+$/),
    reason: z.string().trim().min(10).max(500),
    durationMinutes: z.union([z.literal(5), z.literal(15), z.literal(30)]),
});
export const hqActiveImpersonationResponseSchema = z.object({
    impersonation: hqImpersonationSchema.nullable(),
    canStart: z.boolean(),
    requestId: requestIdSchema,
});
export const hqImpersonationMutationResponseSchema = z.object({
    impersonation: hqImpersonationSchema,
    panelUrl: z.url(),
    requestId: requestIdSchema,
});
export const hqImpersonationEndResponseSchema = z.object({
    ended: z.literal(true),
    hqUrl: z.url(),
    requestId: requestIdSchema,
});
export const hqTeamMemberSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1).max(160),
    email: z.email(),
    role: hqRoleSchema,
    status: z.enum(["active", "suspended"]),
    createdAt: z.iso.datetime(),
});
export const hqTeamResponseSchema = z.object({
    members: z.array(hqTeamMemberSchema),
    canManage: z.boolean(),
    requestId: requestIdSchema,
});
export const hqTeamRoleUpdateRequestSchema = z.object({
    role: hqRoleSchema,
});
export const hqTeamMemberMutationResponseSchema = z.object({
    member: hqTeamMemberSchema,
    requestId: requestIdSchema,
});
export const dutyAssignmentRoleSchema = z.enum([
    "primary_support",
    "backup_support",
    "incident_commander",
    "security",
    "privacy_legal",
    "safeguarding",
    "fleet",
]);
export const dutyRosterAssignmentSchema = z.object({
    role: dutyAssignmentRoleSchema,
    userId: z.string().min(1).nullable(),
    userName: z.string().min(1).max(160).nullable(),
    userEmail: z.email().nullable(),
    hqRole: hqRoleSchema.nullable(),
    userStatus: z.enum(["active", "suspended"]).nullable(),
    updatedAt: z.iso.datetime(),
});
export const dutyRosterResponseSchema = z.object({
    assignments: z.array(dutyRosterAssignmentSchema),
    complete: z.boolean(),
    requestId: requestIdSchema,
});
export const dutyRosterUpdateRequestSchema = z.object({
    assignments: z.object({
        primary_support: z.string().min(1).max(100).nullable(),
        backup_support: z.string().min(1).max(100).nullable(),
        incident_commander: z.string().min(1).max(100).nullable(),
        security: z.string().min(1).max(100).nullable(),
        privacy_legal: z.string().min(1).max(100).nullable(),
        safeguarding: z.string().min(1).max(100).nullable(),
        fleet: z.string().min(1).max(100).nullable(),
    }),
});
export const hqMfaPolicySchema = z.object({
    required: z.boolean(),
    enrolledHqAdministrators: z.number().int().nonnegative(),
    activeHqAdministrators: z.number().int().nonnegative(),
    canEnable: z.boolean(),
    updatedAt: z.iso.datetime(),
});
export const hqMfaPolicyResponseSchema = hqMfaPolicySchema.extend({
    requestId: requestIdSchema,
});
export const hqMfaPolicyUpdateRequestSchema = z.object({
    required: z.boolean(),
});
export const versionResponseSchema = z.object({
    version: z.string().min(1).max(80),
    environment: z.string().min(1).max(40),
    requestId: requestIdSchema,
});
export const schoolMemberRoleSchema = z.enum([
    "school_admin",
    "teacher",
    "technical_guardian",
    "viewer",
]);
export const schoolPermissionSchema = z.enum([
    "school.view",
    "devices.manage",
    "members.manage",
    "curriculum.manage",
    "settings.manage",
    "teacher_duty.manage",
    "teacher_requests.manage",
    "reports.export",
    "support.manage",
]);
const allSchoolPermissions = schoolPermissionSchema.options;
export const schoolRolePermissions = {
    school_admin: allSchoolPermissions,
    teacher: [
        "school.view",
        "teacher_requests.manage",
        "reports.export",
        "support.manage",
    ],
    technical_guardian: ["school.view", "devices.manage", "support.manage"],
    viewer: ["school.view"],
};
export function schoolRoleHasPermission(role, permission) {
    return schoolRolePermissions[role].includes(permission);
}
export const accessContextResponseSchema = z.object({
    user: z.object({
        id: z.string().min(1),
        name: z.string().min(1),
        email: z.email(),
    }),
    hqAccess: z.boolean(),
    hqRole: hqRoleSchema.nullable().optional(),
    hqPermissions: z.array(hqPermissionSchema).optional(),
    impersonation: hqImpersonationSchema.nullable().optional(),
    schoolAccess: z.array(z.object({
        schoolId: z.string().min(1),
        schoolName: z.string().min(1),
        role: schoolMemberRoleSchema,
    })),
    requestId: requestIdSchema,
});
export const stationPairingCodeSchema = z
    .string()
    .regex(/^KT-[A-Z0-9]{4}-[A-Z0-9]{4}$/);
export const stationDeviceTokenSchema = z
    .string()
    .regex(/^ktd_[A-Za-z0-9_-]{43}$/);
export const schoolIdSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z0-9_-]+$/);
export const learningActivityTypeSchema = z.enum([
    "general",
    "homework",
    "test_prep",
    "review",
    "project",
    "language_conversation",
]);
export const learningActivityModeSchema = z.enum([
    "general_tutoring",
    "homework_photo",
    "homework_describe",
    "homework_review_attempt",
    "test_quiz",
    "test_key_topics",
    "test_study_plan",
    "review_explain",
    "review_quiz",
    "review_practice",
    "project_plan",
    "project_research",
    "project_review",
    "language_conversation",
]);
export const learningActivityModesByType = {
    general: ["general_tutoring"],
    homework: ["homework_photo", "homework_describe", "homework_review_attempt"],
    test_prep: ["test_quiz", "test_key_topics", "test_study_plan"],
    review: ["review_explain", "review_quiz", "review_practice"],
    project: ["project_plan", "project_research", "project_review"],
    language_conversation: ["language_conversation"],
};
export function isLearningActivityModeForType(activityType, activityMode) {
    return learningActivityModesByType[activityType].includes(activityMode);
}
export const schoolCatalogResponseSchema = z.object({
    school: z.object({
        id: schoolIdSchema,
        name: z.string().min(1).max(200),
    }),
    grades: z.array(z.object({
        id: z.string().min(1).max(64),
        code: z.string().min(1).max(32),
        displayName: z.string().min(1).max(100),
        subjects: z.array(z.object({
            assignmentId: z.string().min(1).max(64),
            subjectId: z.string().min(1).max(64),
            code: z.string().min(1).max(64),
            displayName: z.string().min(1).max(100),
            category: z.enum(["language", "stem", "humanities", "arts", "other"]),
            activities: z.array(learningActivityTypeSchema),
        })),
    })),
    requestId: requestIdSchema,
});
export const subjectCatalogGradeCodeSchema = z.enum([
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
]);
export const primarySchoolGradeCodes = subjectCatalogGradeCodeSchema.options;
const subjectCatalogGradeSchema = z.object({
    code: subjectCatalogGradeCodeSchema,
    displayName: z.string().min(1).max(100),
});
const subjectCatalogSubjectSchema = z.object({
    id: z.string().min(1).max(64),
    code: z.string().min(1).max(64),
    displayName: z.string().min(1).max(100),
    category: z.enum(["language", "stem", "humanities", "arts", "other"]),
    active: z.boolean(),
    gradeCodes: z.array(subjectCatalogGradeCodeSchema),
});
export const hqSubjectCatalogResponseSchema = z.object({
    grades: z.array(subjectCatalogGradeSchema),
    subjects: z.array(subjectCatalogSubjectSchema),
    requestId: requestIdSchema,
});
export const hqSubjectCatalogUpdateRequestSchema = z.object({
    subjects: z
        .array(z.object({
        id: z.string().min(1).max(64),
        active: z.boolean(),
        gradeCodes: z
            .array(subjectCatalogGradeCodeSchema)
            .max(primarySchoolGradeCodes.length)
            .refine((gradeCodes) => new Set(gradeCodes).size === gradeCodes.length, "Grade codes must be unique"),
    }))
        .min(1)
        .max(100)
        .refine((subjects) => new Set(subjects.map((subject) => subject.id)).size === subjects.length, "Subjects must be unique"),
});
export const schoolCatalogOptionsResponseSchema = z.object({
    subjects: z.array(z.object({
        id: z.string().min(1).max(64),
        code: z.string().min(1).max(64),
        displayName: z.string().min(1).max(100),
        category: z.enum(["language", "stem", "humanities", "arts", "other"]),
    })),
    recommendedByGrade: z
        .array(z.object({
        gradeCode: subjectCatalogGradeCodeSchema,
        subjectIds: z.array(z.string().min(1).max(64)),
    }))
        .default([]),
    activities: z.array(learningActivityTypeSchema),
    requestId: requestIdSchema,
});
const gradeFieldsSchema = z.object({
    code: z.string().trim().min(1).max(32),
    displayName: z.string().trim().min(2).max(100),
});
const gradeSubjectsSchema = z
    .array(z.object({
    subjectId: z.string().min(1).max(64),
    activities: z.array(learningActivityTypeSchema),
}))
    .max(30)
    .refine((subjects) => new Set(subjects.map((item) => item.subjectId)).size === subjects.length, "Subjects must be unique");
export const schoolGradeCreateRequestSchema = gradeFieldsSchema.extend({
    subjects: gradeSubjectsSchema,
});
export const schoolGradeUpdateRequestSchema = gradeFieldsSchema.extend({
    subjects: gradeSubjectsSchema,
});
export const materialStatusSchema = z.enum([
    "uploaded",
    "processing",
    "ready",
    "failed",
    "archived",
]);
export const materialIndexingStatusSchema = z.enum([
    "pending",
    "extracting",
    "awaiting_configuration",
    "indexed",
    "requires_extractor",
    "failed",
    "deleted",
]);
export const schoolMaterialSchema = z.object({
    id: z.string().min(1).max(64),
    seriesId: z.string().min(1).max(64),
    schoolSubjectId: z.string().min(1).max(64),
    gradeId: z.string().min(1).max(64),
    gradeName: z.string().min(1).max(100),
    subjectId: z.string().min(1).max(64),
    subjectName: z.string().min(1).max(100),
    title: z.string().min(1).max(160),
    originalFilename: z.string().min(1).max(180),
    mediaType: z.string().min(1).max(160),
    sizeBytes: z.number().int().positive(),
    version: z.number().int().positive(),
    status: materialStatusSchema,
    indexingStatus: materialIndexingStatusSchema.optional(),
    indexingErrorCode: z.string().max(100).nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    approvedAt: z.string().datetime().nullable(),
});
export const schoolMaterialsResponseSchema = z.object({
    schoolId: schoolIdSchema,
    materials: z.array(schoolMaterialSchema),
    page: cursorPageSchema,
    requestId: requestIdSchema,
});
export const schoolMaterialsQuerySchema = z.object({
    q: z.string().trim().min(2).max(100).optional(),
    schoolSubjectId: z.string().min(1).max(64).optional(),
    status: materialStatusSchema.exclude(["archived"]).optional(),
    sort: z.enum(["title", "updated"]).default("title"),
    direction: z.enum(["asc", "desc"]).default("asc"),
    cursor: cursorSchema.optional(),
    limit: z.coerce.number().int().min(10).max(100).default(50),
});
export const schoolMaterialMutationResponseSchema = z.object({
    material: schoolMaterialSchema,
    requestId: requestIdSchema,
});
export const hintStyleSchema = z.enum(["socratic", "guided", "step_by_step"]);
export const assistanceLevelSchema = z.enum([
    "independent",
    "light",
    "standard",
    "high",
]);
const assistanceLevelRank = {
    independent: 0,
    light: 1,
    standard: 2,
    high: 3,
};
const schoolLearningSettingsBaseSchema = z.object({
    hintStyle: hintStyleSchema,
    defaultAssistanceLevel: assistanceLevelSchema,
    maxAssistanceLevel: assistanceLevelSchema,
    cameraEnabled: z.boolean(),
    voiceEnabled: z.boolean(),
    textChatEnabled: z.boolean(),
    teacherEscalationEnabled: z.boolean(),
    photoRetentionHours: z.number().int().min(0).max(168),
    transcriptRetentionHours: z.number().int().min(0).max(168),
    useHyperlegibleFont: z.boolean(),
    largeTextEnabled: z.boolean(),
    reduceMotion: z.boolean(),
    highContrast: z.boolean(),
    additionalAiRules: z.string().trim().max(2000),
    updatedAt: z.string().datetime(),
});
const validAssistanceRange = (settings) => assistanceLevelRank[settings.defaultAssistanceLevel] <=
    assistanceLevelRank[settings.maxAssistanceLevel];
export const schoolLearningSettingsSchema = schoolLearningSettingsBaseSchema.refine(validAssistanceRange, {
    message: "Default assistance level cannot exceed the school maximum",
    path: ["defaultAssistanceLevel"],
});
export const schoolLearningSettingsUpdateRequestSchema = schoolLearningSettingsBaseSchema
    .omit({
    updatedAt: true,
    photoRetentionHours: true,
    transcriptRetentionHours: true,
})
    .refine(validAssistanceRange, {
    message: "Default assistance level cannot exceed the school maximum",
    path: ["defaultAssistanceLevel"],
})
    .refine((settings) => settings.cameraEnabled ||
    settings.voiceEnabled ||
    settings.textChatEnabled, {
    message: "At least one student interaction method must be enabled",
    path: ["textChatEnabled"],
});
export const schoolLearningSettingsResponseSchema = z.object({
    schoolId: schoolIdSchema,
    settings: schoolLearningSettingsSchema,
    requestId: requestIdSchema,
});
export const stationCatalogResponseSchema = schoolCatalogResponseSchema.extend({
    learningSettings: schoolLearningSettingsSchema,
    displaySettings: z.object({
        defaultTheme: z.enum(["light", "dark"]),
        guardianCodeConfigured: z.boolean(),
    }),
    devicePolicy: z.object({
        kioskRequired: z.boolean(),
        managedConfigurationRequired: z.boolean(),
        supportedPlatforms: z.array(z.enum(["ios", "android"])).length(2),
    }),
    features: z.record(z.string().min(1).max(100), z.boolean()),
});
export const featureFlagRolloutModeSchema = z.enum([
    "off",
    "internal",
    "pilot",
    "percentage",
    "on",
]);
export const featureFlagUpdateRequestSchema = z
    .object({
    rolloutMode: featureFlagRolloutModeSchema,
    rolloutPercentage: z.number().int().min(0).max(100),
})
    .refine((value) => value.rolloutMode !== "percentage" || value.rolloutPercentage > 0, { path: ["rolloutPercentage"], message: "Percentage must be positive" });
export const featureFlagSchema = z.object({
    key: z.string().min(1).max(100),
    displayName: z.string().min(1).max(200),
    description: z.string().min(1).max(500),
    rolloutMode: featureFlagRolloutModeSchema,
    rolloutPercentage: z.number().int().min(0).max(100),
    overrideCount: z.number().int().min(0),
    updatedAt: z.iso.datetime(),
});
export const featureFlagsResponseSchema = z.object({
    flags: z.array(featureFlagSchema),
    requestId: requestIdSchema,
});
export const schoolMemberSchema = z.object({
    id: z.string().min(1).max(64),
    kind: z.enum(["member", "invitation"]),
    userId: z.string().min(1).max(64).nullable(),
    displayName: z.string().min(1).max(200),
    email: z.email(),
    role: schoolMemberRoleSchema,
    status: z.enum(["active", "invited"]),
    lastLoginAt: z.iso.datetime().nullable(),
    invitedAt: z.iso.datetime().nullable(),
});
export const schoolMembersResponseSchema = z.object({
    schoolId: schoolIdSchema,
    members: z.array(schoolMemberSchema),
    page: cursorPageSchema,
    requestId: requestIdSchema,
});
export const schoolMembersQuerySchema = z.object({
    q: z.string().trim().min(2).max(100).optional(),
    role: schoolMemberRoleSchema.optional(),
    status: z.enum(["active", "invited"]).optional(),
    sort: z.enum(["name", "lastLogin"]).default("name"),
    direction: z.enum(["asc", "desc"]).default("asc"),
    cursor: cursorSchema.optional(),
    limit: z.coerce.number().int().min(10).max(100).default(50),
});
export const schoolInvitationCreateRequestSchema = z.object({
    displayName: z.string().trim().min(2).max(200),
    email: z.string().trim().toLowerCase().pipe(z.email()),
    role: schoolMemberRoleSchema,
});
export const schoolInvitationCreateResponseSchema = z.object({
    invitation: schoolMemberSchema,
    delivery: z.enum(["email", "development_link"]),
    acceptanceUrl: z.url().nullable(),
    requestId: requestIdSchema,
});
export const schoolMemberRoleUpdateRequestSchema = z.object({
    role: schoolMemberRoleSchema,
});
export const schoolMemberMutationResponseSchema = z.object({
    member: schoolMemberSchema,
    requestId: requestIdSchema,
});
export const schoolInvitationDetailsResponseSchema = z.object({
    schoolName: z.string().min(1).max(200),
    email: z.email(),
    displayName: z.string().min(1).max(200),
    role: schoolMemberRoleSchema,
    expiresAt: z.iso.datetime(),
    requestId: requestIdSchema,
});
export const schoolInvitationAcceptRequestSchema = z.object({
    token: z.string().min(32).max(128),
    displayName: z.string().trim().min(2).max(200),
    password: z.string().min(12).max(128),
});
export const schoolInvitationAcceptResponseSchema = z.object({
    accepted: z.literal(true),
    loginEmail: z.email(),
    requestId: requestIdSchema,
});
export const schoolProfileSchema = z.object({
    schoolId: schoolIdSchema,
    name: z.string().min(2).max(200),
    legalName: z.string().min(2).max(240).nullable(),
    address: z.string().min(3).max(300).nullable(),
    contactEmail: z.email(),
    timezone: z.string().min(1).max(64),
    locale: z.string().min(2).max(16),
    defaultTheme: z.enum(["light", "dark"]),
    hasGuardianCode: z.boolean(),
    photoRetentionHours: z.number().int().min(0).max(168),
    transcriptRetentionHours: z.number().int().min(0).max(168),
    updatedAt: z.iso.datetime(),
});
export const schoolProfileResponseSchema = z.object({
    profile: schoolProfileSchema,
    requestId: requestIdSchema,
});
export const schoolProfileUpdateRequestSchema = z.object({
    name: z.string().trim().min(2).max(200),
    legalName: z.string().trim().min(2).max(240).nullable(),
    address: z.string().trim().min(3).max(300).nullable(),
    contactEmail: z.string().trim().toLowerCase().pipe(z.email()),
    timezone: z.enum(["Europe/Warsaw", "Europe/London", "Europe/Berlin"]),
    locale: z.enum(["pl-PL", "en-GB", "de-DE"]),
    defaultTheme: z.enum(["light", "dark"]),
});
export const schoolGuardianCodeUpdateRequestSchema = z.object({
    code: z.string().regex(/^\d{6}$/),
});
export const schoolGuardianCodeUpdateResponseSchema = z.object({
    updated: z.literal(true),
    updatedAt: z.iso.datetime(),
    requestId: requestIdSchema,
});
export const stationGuardianCodeVerifyRequestSchema = z.object({
    code: z.string().regex(/^\d{6}$/),
});
export const stationGuardianCodeVerifyResponseSchema = z.object({
    valid: z.boolean(),
    lockedUntil: z.iso.datetime().nullable(),
    requestId: requestIdSchema,
});
export const deviceStatusSchema = z.enum([
    "pending",
    "online",
    "offline",
    "maintenance",
    "retired",
]);
export const devicePlatformSchema = z.enum(["ios", "android"]);
export const deviceReleaseRingSchema = z.enum([
    "internal",
    "pilot",
    "production",
]);
export const deviceLifecycleStateSchema = z.enum([
    "ordered",
    "warehouse",
    "provisioning",
    "shipping",
    "deployed",
    "service",
    "returned",
    "retired",
]);
export const deviceManagementStateSchema = z.enum([
    "unmanaged",
    "enrolling",
    "managed",
    "non_compliant",
    "retired",
]);
export const deviceSummarySchema = z.object({
    id: z.string().min(1).max(64),
    schoolId: schoolIdSchema.nullable(),
    schoolName: z.string().min(1).max(200).nullable(),
    name: z.string().min(1).max(200),
    location: z.string().min(1).max(200).nullable(),
    platform: devicePlatformSchema,
    releaseRing: deviceReleaseRingSchema.default("production"),
    serialNumber: z.string().min(1).max(128).nullable(),
    lifecycleState: deviceLifecycleStateSchema,
    assetTag: z.string().min(1).max(64).nullable(),
    hardwareModel: z.string().min(1).max(200).nullable(),
    storageGb: z.number().int().min(8).max(4096).nullable(),
    purchasedAt: z.iso.date().nullable(),
    warrantyExpiresAt: z.iso.date().nullable(),
    managed: z.boolean(),
    managementProvider: z.string().min(2).max(80).nullable().optional(),
    managementExternalId: z.string().min(1).max(256).nullable().optional(),
    managementState: deviceManagementStateSchema.optional(),
    managementModel: z.string().min(1).max(200).nullable().optional(),
    managementOsVersion: z.string().min(1).max(80).nullable().optional(),
    managementLastSyncedAt: z.iso.datetime().nullable().optional(),
    managementIdentityVerified: z.boolean().optional(),
    status: deviceStatusSchema,
    appVersion: z.string().min(1).max(40).nullable(),
    osVersion: z.string().min(1).max(40).nullable(),
    lastSeenAt: z.iso.datetime().nullable(),
    pairedAt: z.iso.datetime().nullable(),
});
export const deviceProvisioningStepKeys = [
    "inventory",
    "management",
    "schoolAssignment",
    "deployment",
    "pairing",
    "firstContact",
];
export function getDeviceProvisioningProgress(device, now = new Date()) {
    const lastSeenAt = device.lastSeenAt
        ? new Date(device.lastSeenAt).getTime()
        : Number.NaN;
    const checks = {
        inventory: Boolean(device.serialNumber),
        management: Boolean(device.managementState === "managed" &&
            device.managementProvider &&
            device.managementExternalId &&
            device.managementLastSyncedAt),
        schoolAssignment: Boolean(device.schoolId),
        deployment: device.lifecycleState === "deployed",
        pairing: Boolean(device.pairedAt && device.managementIdentityVerified),
        firstContact: Boolean(device.status === "online" &&
            device.appVersion &&
            Number.isFinite(lastSeenAt) &&
            now.getTime() - lastSeenAt <= 30 * 60_000),
    };
    const currentKey = deviceProvisioningStepKeys.find((key) => !checks[key]);
    const steps = deviceProvisioningStepKeys.map((key) => ({
        key,
        status: checks[key]
            ? "complete"
            : key === currentKey
                ? "current"
                : "pending",
    }));
    const completedCount = steps.filter((step) => step.status === "complete").length;
    return {
        steps,
        completedCount,
        totalCount: steps.length,
        ready: completedCount === steps.length,
        currentKey: currentKey ?? null,
    };
}
export const schoolDevicesResponseSchema = z.object({
    schoolId: schoolIdSchema,
    devices: z.array(deviceSummarySchema),
    generatedAt: z.iso.datetime(),
    requestId: requestIdSchema,
});
export const hqFleetQuerySchema = z.object({
    q: z.string().trim().min(2).max(100).optional(),
    platform: devicePlatformSchema.optional(),
    status: deviceStatusSchema.optional(),
    lifecycleState: deviceLifecycleStateSchema.optional(),
    releaseRing: deviceReleaseRingSchema.optional(),
    schoolId: schoolIdSchema.optional(),
    managed: z
        .enum(["true", "false"])
        .transform((value) => value === "true")
        .optional(),
    sort: z
        .enum(["name", "school", "status", "lifecycle", "lastSeen"])
        .default("school"),
    direction: z.enum(["asc", "desc"]).default("asc"),
    cursor: cursorSchema.optional(),
    limit: z.coerce.number().int().min(10).max(100).default(50),
});
export const hqFleetResponseSchema = z.object({
    summary: z.object({
        total: z.number().int().min(0),
        platforms: z.object({
            ios: z.number().int().min(0),
            android: z.number().int().min(0),
        }),
        managed: z.number().int().min(0),
        online: z.number().int().min(0),
        attention: z.number().int().min(0),
        retired: z.number().int().min(0),
    }),
    devices: z.array(deviceSummarySchema),
    page: cursorPageSchema,
    generatedAt: z.iso.datetime(),
    requestId: requestIdSchema,
});
export const stationReleaseStageSchema = z.enum([
    "internal",
    "pilot",
    "ten_percent",
    "fifty_percent",
    "production",
]);
export const stationReleaseStatusSchema = z.enum([
    "draft",
    "active",
    "paused",
    "completed",
    "cancelled",
]);
export const stationReleaseEventSchema = z.object({
    id: z.string().min(1).max(128),
    action: z.enum([
        "created",
        "activated",
        "promoted",
        "paused",
        "resumed",
        "completed",
        "cancelled",
    ]),
    previousStage: stationReleaseStageSchema.nullable(),
    nextStage: stationReleaseStageSchema,
    actorName: z.string().min(1).max(200),
    reason: z.string().min(3).max(500),
    createdAt: z.iso.datetime(),
});
export const stationReleaseSchema = z.object({
    id: z.string().min(1).max(128),
    platform: devicePlatformSchema,
    version: z.string().min(1).max(40),
    buildNumber: z.string().regex(/^[1-9][0-9]{0,17}$/),
    artifactSha256: z.string().regex(/^[0-9a-f]{64}$/),
    distributionReference: z.string().min(1).max(240),
    mdmProvider: z.string().min(2).max(80),
    mdmCampaignId: z.string().min(1).max(240),
    status: stationReleaseStatusSchema,
    currentStage: stationReleaseStageSchema,
    notes: z.string().min(3).max(2000),
    createdBy: z.string().min(1).max(200),
    targetDevices: z.number().int().min(0),
    managedTargets: z.number().int().min(0),
    updatedDevices: z.number().int().min(0),
    attentionDevices: z.number().int().min(0),
    events: z.array(stationReleaseEventSchema).max(100),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const stationReleaseCreateRequestSchema = z.object({
    platform: devicePlatformSchema,
    version: z
        .string()
        .trim()
        .regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/),
    buildNumber: z
        .string()
        .trim()
        .regex(/^[1-9][0-9]{0,17}$/),
    artifactSha256: z
        .string()
        .trim()
        .toLowerCase()
        .regex(/^[0-9a-f]{64}$/),
    distributionReference: z.string().trim().min(3).max(240),
    mdmProvider: z.string().trim().min(2).max(80),
    mdmCampaignId: z.string().trim().min(1).max(240),
    notes: z.string().trim().min(3).max(2000),
});
export const stationReleaseTransitionRequestSchema = z.object({
    action: z.enum([
        "activate",
        "promote",
        "pause",
        "resume",
        "complete",
        "cancel",
    ]),
    reason: z.string().trim().min(10).max(500),
});
export const stationReleasesResponseSchema = z.object({
    releases: z.array(stationReleaseSchema),
    requestId: requestIdSchema,
});
export const stationReleaseMutationResponseSchema = z.object({
    release: stationReleaseSchema,
    requestId: requestIdSchema,
});
export const operatingSystemVersionSchema = z
    .string()
    .trim()
    .regex(/^\d{1,3}(?:\.\d{1,3}){0,2}$/);
export const devicePlatformPolicySchema = z.object({
    platform: devicePlatformSchema,
    minimumOsVersion: operatingSystemVersionSchema.nullable(),
    compliance: z.object({
        total: z.number().int().min(0),
        compliant: z.number().int().min(0),
        updateRequired: z.number().int().min(0),
        unknown: z.number().int().min(0),
    }),
    updatedBy: z.string().min(1).max(200).nullable(),
    updatedAt: z.iso.datetime(),
});
export const devicePlatformPoliciesResponseSchema = z.object({
    policies: z.array(devicePlatformPolicySchema).length(2),
    requestId: requestIdSchema,
});
export const devicePlatformPolicyUpdateRequestSchema = z.object({
    minimumOsVersion: operatingSystemVersionSchema.nullable(),
    reason: z.string().trim().min(10).max(500),
});
export const devicePlatformPolicyMutationResponseSchema = z.object({
    policy: devicePlatformPolicySchema,
    requestId: requestIdSchema,
});
export const deviceEventSchema = z.object({
    id: z.string().min(1).max(128),
    eventType: z.enum([
        "details_updated",
        "status_changed",
        "school_reassigned",
        "lifecycle_changed",
    ]),
    actorName: z.string().min(1).max(200),
    previousSchoolName: z.string().min(1).max(200).nullable(),
    nextSchoolName: z.string().min(1).max(200).nullable(),
    description: z.string().min(1).max(500),
    createdAt: z.iso.datetime(),
});
export const hqDeviceDetailsResponseSchema = z.object({
    device: deviceSummarySchema,
    events: z.array(deviceEventSchema),
    activeLesson: z.boolean(),
    requestId: requestIdSchema,
});
export const hqDeviceCreateRequestSchema = z
    .object({
    name: z.string().trim().min(2).max(200),
    platform: devicePlatformSchema,
    serialNumber: z.string().trim().min(3).max(128),
    lifecycleState: z.enum(["ordered", "warehouse", "provisioning"]),
    releaseRing: deviceReleaseRingSchema,
    assetTag: z.string().trim().min(1).max(64).nullable(),
    hardwareModel: z.string().trim().min(1).max(200).nullable(),
    storageGb: z.number().int().min(8).max(4096).nullable(),
    purchasedAt: z.iso.date().nullable(),
    warrantyExpiresAt: z.iso.date().nullable(),
})
    .refine((value) => !value.purchasedAt ||
    !value.warrantyExpiresAt ||
    value.warrantyExpiresAt >= value.purchasedAt, {
    path: ["warrantyExpiresAt"],
    message: "Warranty cannot expire before purchase",
});
export const hqDeviceUpdateRequestSchema = z.object({
    schoolId: schoolIdSchema.nullable(),
    name: z.string().trim().min(2).max(200),
    location: z.string().trim().max(200).nullable(),
    status: deviceStatusSchema,
    lifecycleState: deviceLifecycleStateSchema.optional(),
    releaseRing: deviceReleaseRingSchema.optional(),
    lifecycleNote: z.string().trim().min(3).max(500).nullable().optional(),
    assetTag: z.string().trim().min(1).max(64).nullable().optional(),
    hardwareModel: z.string().trim().min(1).max(200).nullable().optional(),
    storageGb: z.number().int().min(8).max(4096).nullable().optional(),
    purchasedAt: z.iso.date().nullable().optional(),
    warrantyExpiresAt: z.iso.date().nullable().optional(),
});
export const hqDeviceReEnrollmentRequestSchema = z.object({
    schoolId: schoolIdSchema,
    name: z.string().trim().min(2).max(200),
    location: z.string().trim().min(1).max(200).nullable(),
    note: z.string().trim().min(10).max(500),
    wipeConfirmed: z.literal(true),
});
export const deviceAccessoryTypeSchema = z.enum([
    "charger",
    "cable",
    "case",
    "stand",
    "headphones",
    "security_lock",
    "other",
]);
export const deviceAccessoryStatusSchema = z.enum([
    "assigned",
    "missing",
    "damaged",
    "returned",
    "replaced",
]);
export const deviceAccessorySchema = z.object({
    id: z.string().min(1).max(128),
    type: deviceAccessoryTypeSchema,
    label: z.string().min(1).max(200),
    serialNumber: z.string().min(1).max(128).nullable(),
    status: deviceAccessoryStatusSchema,
    costGrosz: z.number().int().min(0).max(100_000_000).nullable(),
    assignedAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const deviceConditionCheckTypeSchema = z.enum([
    "received",
    "pre_shipping",
    "delivery",
    "return",
    "service",
]);
export const deviceConditionResultSchema = z.enum([
    "passed",
    "issues",
    "failed",
]);
export const deviceConditionCheckSchema = z.object({
    id: z.string().min(1).max(128),
    type: deviceConditionCheckTypeSchema,
    result: deviceConditionResultSchema,
    screenOk: z.boolean(),
    cameraOk: z.boolean(),
    microphoneOk: z.boolean(),
    speakerOk: z.boolean(),
    chargingOk: z.boolean(),
    notes: z.string().min(1).max(2000),
    checkedBy: z.string().min(1).max(200),
    createdAt: z.iso.datetime(),
});
export const deviceServiceOrderStatusSchema = z.enum([
    "open",
    "diagnosing",
    "waiting_parts",
    "repairing",
    "resolved",
    "cancelled",
]);
export const deviceServiceOrderPrioritySchema = z.enum([
    "low",
    "normal",
    "high",
    "critical",
]);
export const deviceReplacementStatusSchema = z.enum([
    "not_required",
    "requested",
    "reserved",
    "shipped",
    "delivered",
    "returned",
    "cancelled",
]);
export const deviceReplacementCandidateSchema = z.object({
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
    platform: devicePlatformSchema,
    lifecycleState: deviceLifecycleStateSchema,
    assetTag: z.string().min(1).max(64).nullable(),
});
export const deviceServiceOrderSchema = z.object({
    id: z.string().min(1).max(128),
    status: deviceServiceOrderStatusSchema,
    priority: deviceServiceOrderPrioritySchema,
    vendor: z.string().min(1).max(200).nullable(),
    externalReference: z.string().min(1).max(200).nullable(),
    description: z.string().min(3).max(2000),
    resolution: z.string().min(3).max(2000).nullable(),
    costGrosz: z.number().int().min(0).max(100_000_000).nullable(),
    assignedTo: z.string().min(1).max(200).nullable(),
    openedBy: z.string().min(1).max(200),
    openedAt: z.iso.datetime(),
    resolvedAt: z.iso.datetime().nullable(),
    replacementRequired: z.boolean().default(false),
    replacementStatus: deviceReplacementStatusSchema.default("not_required"),
    replacementDeviceId: z.string().min(1).max(64).nullable().default(null),
    replacementDeviceName: z.string().min(1).max(200).nullable().default(null),
    replacementDueAt: z.iso.datetime().nullable().default(null),
    replacementDeliveredAt: z.iso.datetime().nullable().default(null),
    updatedAt: z.iso.datetime(),
});
export const deviceOperationAttachmentTargetTypeSchema = z.enum([
    "condition_check",
    "service_order",
]);
export const deviceOperationAttachmentSchema = z.object({
    id: z.string().min(1).max(128),
    targetType: deviceOperationAttachmentTargetTypeSchema,
    targetId: z.string().min(1).max(128),
    originalFilename: z.string().min(1).max(180),
    mediaType: z.enum(["application/pdf", "image/jpeg", "image/png"]),
    sizeBytes: z
        .number()
        .int()
        .min(1)
        .max(10 * 1024 * 1024),
    uploadedBy: z.string().min(1).max(200),
    createdAt: z.iso.datetime(),
});
export const hqDeviceOperationsResponseSchema = z.object({
    deviceId: z.string().min(1).max(64),
    accessories: z.array(deviceAccessorySchema),
    conditionChecks: z.array(deviceConditionCheckSchema),
    serviceOrders: z.array(deviceServiceOrderSchema),
    replacementCandidates: z.array(deviceReplacementCandidateSchema).default([]),
    attachments: z.array(deviceOperationAttachmentSchema),
    requestId: requestIdSchema,
});
export const deviceAccessoryCreateRequestSchema = z.object({
    type: deviceAccessoryTypeSchema,
    label: z.string().trim().min(1).max(200),
    serialNumber: z.string().trim().min(1).max(128).nullable(),
    status: deviceAccessoryStatusSchema,
    costGrosz: z.number().int().min(0).max(100_000_000).nullable(),
});
export const deviceAccessoryUpdateRequestSchema = deviceAccessoryCreateRequestSchema;
export const deviceConditionCheckCreateRequestSchema = z.object({
    type: deviceConditionCheckTypeSchema,
    result: deviceConditionResultSchema,
    screenOk: z.boolean(),
    cameraOk: z.boolean(),
    microphoneOk: z.boolean(),
    speakerOk: z.boolean(),
    chargingOk: z.boolean(),
    notes: z.string().trim().min(3).max(2000),
});
export const deviceServiceOrderCreateRequestSchema = z
    .object({
    priority: deviceServiceOrderPrioritySchema,
    vendor: z.string().trim().min(1).max(200).nullable(),
    externalReference: z.string().trim().min(1).max(200).nullable(),
    description: z.string().trim().min(3).max(2000),
    assignedTo: z.string().trim().min(1).max(200).nullable(),
    replacementRequired: z.boolean().default(false),
    replacementDueAt: z.iso.datetime().nullable().default(null),
})
    .superRefine((value, context) => {
    if (value.replacementRequired && value.replacementDueAt === null) {
        context.addIssue({
            code: "custom",
            path: ["replacementDueAt"],
            message: "Replacement due date is required when a replacement is needed.",
        });
    }
    if (!value.replacementRequired && value.replacementDueAt !== null) {
        context.addIssue({
            code: "custom",
            path: ["replacementDueAt"],
            message: "Replacement due date requires a replacement order.",
        });
    }
});
export const deviceServiceOrderUpdateRequestSchema = z
    .object({
    status: deviceServiceOrderStatusSchema,
    vendor: z.string().trim().min(1).max(200).nullable(),
    externalReference: z.string().trim().min(1).max(200).nullable(),
    resolution: z.string().trim().min(3).max(2000).nullable(),
    costGrosz: z.number().int().min(0).max(100_000_000).nullable(),
    assignedTo: z.string().trim().min(1).max(200).nullable(),
    replacementStatus: deviceReplacementStatusSchema.optional(),
    replacementDeviceId: z.string().trim().min(1).max(64).nullable().optional(),
    replacementDueAt: z.iso.datetime().nullable().optional(),
})
    .superRefine((value, context) => {
    if (value.status === "resolved" && value.resolution === null) {
        context.addIssue({
            code: "custom",
            path: ["resolution"],
            message: "Resolution is required for resolved service orders.",
        });
    }
});
export const managedDeviceIdentitySchema = z.object({
    provider: z.string().trim().min(2).max(80),
    externalId: z.string().trim().min(1).max(256),
});
export const stationHeartbeatRequestSchema = z.object({
    appVersion: z.string().trim().min(1).max(40),
    osVersion: z.string().trim().min(1).max(40),
    management: managedDeviceIdentitySchema.optional(),
});
export const deviceCommandTypeSchema = z.enum(["refresh_configuration"]);
export const deviceCommandStatusSchema = z.enum([
    "pending",
    "delivered",
    "applied",
    "failed",
]);
export const stationDeviceCommandSchema = z.object({
    id: z.string().min(1).max(128),
    type: deviceCommandTypeSchema,
    createdAt: z.iso.datetime(),
});
export const schoolDeviceCommandCreateRequestSchema = z.object({
    type: deviceCommandTypeSchema,
});
export const schoolDeviceCommandResponseSchema = z.object({
    command: z.object({
        id: z.string().min(1).max(128),
        deviceId: z.string().min(1).max(64),
        commandType: deviceCommandTypeSchema,
        status: deviceCommandStatusSchema,
        createdAt: z.iso.datetime(),
        deliveredAt: z.iso.datetime().nullable(),
        acknowledgedAt: z.iso.datetime().nullable(),
    }),
    requestId: requestIdSchema,
});
export const stationDeviceCommandAckRequestSchema = z.object({
    status: z.enum(["applied", "failed"]),
    failureReason: z.string().trim().min(1).max(500).nullable().default(null),
});
export const stationDeviceCommandAckResponseSchema = z.object({
    commandId: z.string().min(1).max(128),
    status: z.enum(["applied", "failed"]),
    requestId: requestIdSchema,
});
export const stationHeartbeatResponseSchema = z.object({
    deviceId: z.string().min(1).max(64),
    schoolId: schoolIdSchema,
    status: z.enum(["online", "maintenance"]),
    requiredAction: z.enum(["none", "update_required"]),
    minimumAppVersion: z.string().trim().min(1).max(40).nullable(),
    minimumOsVersion: operatingSystemVersionSchema.nullable(),
    acceptedAt: z.iso.datetime(),
    nextHeartbeatSeconds: z.number().int().min(60).max(900),
    commands: z.array(stationDeviceCommandSchema).max(10),
    requestId: requestIdSchema,
});
export const limitValuesSchema = z.object({
    monthlyLessons: z.number().int().min(1).max(1_000_000),
    monthlyRealtimeMinutes: z.number().int().min(1).max(10_000_000),
    monthlyImageAnalyses: z.number().int().min(1).max(1_000_000),
    monthlyTextMessages: z.number().int().min(1).max(10_000_000).default(10_000),
    maxLessonMinutes: z.number().int().min(1).max(1_440).default(60),
    maxConcurrentLessons: z.number().int().min(1).max(10_000).default(20),
    maxR2StorageBytes: z
        .number()
        .int()
        .min(1_048_576)
        .max(10_000_000_000_000)
        .default(5_368_709_120),
    maxMaterials: z.number().int().min(1).max(1_000_000).default(500),
    monthlyAiBudgetMicrousd: z
        .number()
        .int()
        .min(1)
        .max(10_000_000_000_000)
        .default(300_000_000),
    warningPercent: z.number().int().min(1).max(100),
    hardStop: z.boolean(),
});
export const schoolLimitOverridesSchema = z.object({
    monthlyLessons: limitValuesSchema.shape.monthlyLessons.nullable(),
    monthlyRealtimeMinutes: limitValuesSchema.shape.monthlyRealtimeMinutes.nullable(),
    monthlyImageAnalyses: limitValuesSchema.shape.monthlyImageAnalyses.nullable(),
    monthlyTextMessages: limitValuesSchema.shape.monthlyTextMessages
        .nullable()
        .default(null),
    maxLessonMinutes: limitValuesSchema.shape.maxLessonMinutes
        .nullable()
        .default(null),
    maxConcurrentLessons: limitValuesSchema.shape.maxConcurrentLessons
        .nullable()
        .default(null),
    maxR2StorageBytes: limitValuesSchema.shape.maxR2StorageBytes
        .nullable()
        .default(null),
    maxMaterials: limitValuesSchema.shape.maxMaterials.nullable().default(null),
    monthlyAiBudgetMicrousd: limitValuesSchema.shape.monthlyAiBudgetMicrousd
        .nullable()
        .default(null),
    warningPercent: limitValuesSchema.shape.warningPercent.nullable(),
    hardStop: limitValuesSchema.shape.hardStop.nullable(),
});
export const limitResourceSchema = z.enum([
    "lessons",
    "realtime_seconds",
    "image_analyses",
    "text_messages",
    "concurrent_lessons",
    "storage_bytes",
    "materials",
    "ai_cost_microusd",
]);
export const limitUsageMetricSchema = z.object({
    used: z.number().int().min(0),
    limit: z.number().int().min(1),
    remaining: z.number().int().min(0),
    percent: z.number().min(0),
    status: z.enum(["ok", "warning", "exceeded"]),
});
export function forecastLimitUsage(metric, periodStartsAt, periodEndsAt, now = new Date()) {
    const dayMs = 24 * 60 * 60 * 1000;
    const start = new Date(periodStartsAt).getTime();
    const end = new Date(periodEndsAt).getTime();
    const observedUntil = Math.min(Math.max(now.getTime(), start), end);
    const elapsed = observedUntil - start;
    const duration = end - start;
    const basisDays = Math.max(0, Math.floor(elapsed / dayMs));
    const insufficient = !Number.isFinite(start) ||
        !Number.isFinite(end) ||
        duration <= 0 ||
        elapsed < dayMs ||
        metric.used === 0;
    if (insufficient) {
        return {
            projectedUsedAtPeriodEnd: null,
            projectedPercentAtPeriodEnd: null,
            exhaustionAt: null,
            expectedToExceed: metric.used >= metric.limit,
            basisDays,
            confidence: "insufficient",
        };
    }
    const projectedUsedAtPeriodEnd = Math.max(metric.used, Math.round((metric.used * duration) / elapsed));
    const projectedPercentAtPeriodEnd = Math.round((projectedUsedAtPeriodEnd / metric.limit) * 1_000) / 10;
    const exhaustionTime = start + (elapsed * metric.limit) / metric.used;
    const expectedToExceed = metric.used >= metric.limit || exhaustionTime < end;
    return {
        projectedUsedAtPeriodEnd,
        projectedPercentAtPeriodEnd,
        exhaustionAt: expectedToExceed
            ? new Date(Math.min(Math.max(exhaustionTime, start), end)).toISOString()
            : null,
        expectedToExceed,
        basisDays,
        confidence: basisDays < 7 ? "early" : "established",
    };
}
export const schoolLimitUsageSchema = z.object({
    periodStartsAt: z.iso.datetime(),
    periodEndsAt: z.iso.datetime(),
    lessons: limitUsageMetricSchema,
    realtimeSeconds: limitUsageMetricSchema,
    imageAnalyses: limitUsageMetricSchema,
    textMessages: limitUsageMetricSchema,
    activeLessons: limitUsageMetricSchema,
    storageBytes: limitUsageMetricSchema,
    materials: limitUsageMetricSchema,
    aiCostMicrousd: limitUsageMetricSchema,
    blockingResource: limitResourceSchema.nullable(),
});
export const openAiVoiceSchema = z.enum([
    "alloy",
    "ash",
    "ballad",
    "coral",
    "echo",
    "sage",
    "shimmer",
    "verse",
    "marin",
    "cedar",
]);
export const realtimeTurnDetectionSchema = z.enum([
    "server_vad",
    "semantic_vad",
]);
export const semanticVadEagernessSchema = z.enum([
    "low",
    "medium",
    "high",
    "auto",
]);
export const openAiModelIdSchema = z
    .string()
    .trim()
    .min(3)
    .max(120)
    .regex(/^[A-Za-z0-9._:-]+$/);
export const openAiProjectIdSchema = z
    .string()
    .trim()
    .min(8)
    .max(128)
    .regex(/^proj_[A-Za-z0-9_-]+$/);
export const openAiApiKeySchema = z
    .string()
    .trim()
    .min(20)
    .max(512)
    .regex(/^sk-[A-Za-z0-9._-]+$/);
export const openAiReasoningEffortSchema = z.enum([
    "none",
    "low",
    "medium",
    "high",
    "xhigh",
    "max",
]);
export const schoolOpenAiCredentialCreateSchema = z.object({
    apiKey: openAiApiKeySchema,
});
export const teacherPromptVersionSchema = z.enum([
    "kt-teacher-v1",
    "kt-teacher-v2",
]);
export const platformAiSettingsSchema = z.object({
    realtimeModel: openAiModelIdSchema,
    textVisionModel: openAiModelIdSchema,
    teacherPromptVersion: teacherPromptVersionSchema,
    teacherContext: z.string().trim().max(4000),
    voice: openAiVoiceSchema,
    turnDetection: realtimeTurnDetectionSchema,
    semanticEagerness: semanticVadEagernessSchema,
    vadThreshold: z.number().min(0).max(1),
    silenceDurationMs: z.number().int().min(100).max(5000),
    prefixPaddingMs: z.number().int().min(0).max(5000),
    speechSpeed: z.number().min(0.25).max(1.5),
    maxOutputTokens: z.number().int().min(1).max(4096),
    dailyBudgetUsd: z.number().min(0).max(1_000_000),
    enabled: z.boolean(),
});
export const schoolAiSettingsOverridesSchema = z.object({
    moderationEnabled: z.boolean().nullable(),
    contentRetentionDays: z.number().int().min(0).max(365).nullable(),
    photoRetentionHours: z.number().int().min(0).max(168).nullable(),
});
export const schoolAiSettingsUpdateRequestSchema = schoolAiSettingsOverridesSchema.extend({
    credential: z.discriminatedUnion("action", [
        z.object({ action: z.literal("keep") }),
        z.object({
            action: z.literal("replace"),
            apiKey: openAiApiKeySchema,
        }),
        z.object({ action: z.literal("remove") }),
    ]),
});
export const schoolAiSettingsResponseSchema = z.object({
    schoolId: schoolIdSchema,
    overrides: schoolAiSettingsOverridesSchema,
    effective: platformAiSettingsSchema.extend({
        textVisionReasoningEffort: openAiReasoningEffortSchema,
        moderationEnabled: z.boolean(),
        contentRetentionDays: z.number().int().min(0).max(365),
        photoRetentionHours: z.number().int().min(0).max(168),
    }),
    credential: z.object({
        configured: z.boolean(),
        apiKeyId: z.string().min(1).max(128).nullable(),
        lastFour: z.string().length(4).nullable(),
        updatedAt: z.iso.datetime().nullable(),
    }),
    updatedAt: z.iso.datetime().nullable(),
    requestId: requestIdSchema,
});
export const openAiModelsResponseSchema = z.object({
    models: z.array(openAiModelIdSchema).max(1000),
    requestId: requestIdSchema,
});
export const hqConversationContentSchema = z.object({
    id: z.string().min(1).max(128),
    lessonId: z.string().min(1).max(128),
    deviceName: z.string().min(1).max(200),
    role: z.enum(["user", "assistant"]),
    modality: z.enum(["text", "voice"]),
    content: z.string().min(1).max(12_000),
    disposition: z.string().min(1).max(40).nullable(),
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
});
export const hqConversationPhotoSchema = z.object({
    id: z.string().min(1).max(128),
    lessonId: z.string().min(1).max(128),
    deviceName: z.string().min(1).max(200),
    mimeType: z.enum(["image/jpeg", "image/png", "image/webp"]),
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
});
export const hqConversationArchiveResponseSchema = z.object({
    schoolId: schoolIdSchema,
    contents: z.array(hqConversationContentSchema).max(500),
    photos: z.array(hqConversationPhotoSchema).max(200),
    requestId: requestIdSchema,
});
export const platformAiSettingsResponseSchema = z.object({
    settings: platformAiSettingsSchema,
    secret: z.object({
        configured: z.boolean(),
        source: z.literal("worker_secret"),
    }),
    revision: z.number().int().min(1),
    canRollback: z.boolean(),
    updatedAt: z.iso.datetime(),
    lastTest: z
        .object({
        status: z.enum(["success", "failure"]),
        testedAt: z.iso.datetime(),
        message: z.string().max(500),
    })
        .nullable(),
    requestId: requestIdSchema,
});
export const platformCostSettingsSchema = z.object({
    usdToPln: z.number().min(0.01).max(100).default(4),
    textInputUsdPerMillion: z.number().min(0).max(1_000_000),
    textOutputUsdPerMillion: z.number().min(0).max(1_000_000),
    realtimeTextInputUsdPerMillion: z.number().min(0).max(1_000_000),
    realtimeTextCachedInputUsdPerMillion: z.number().min(0).max(1_000_000),
    realtimeTextOutputUsdPerMillion: z.number().min(0).max(1_000_000),
    realtimeAudioInputUsdPerMillion: z.number().min(0).max(1_000_000),
    realtimeAudioCachedInputUsdPerMillion: z.number().min(0).max(1_000_000),
    realtimeAudioOutputUsdPerMillion: z.number().min(0).max(1_000_000),
    embeddingInputUsdPerMillion: z.number().min(0).max(1_000_000),
    storageUsdPerGibMonth: z.number().min(0).max(1_000_000),
    emailUsdPerThousand: z.number().min(0).max(1_000_000),
    hostingBaseMonthlyPln: z.number().min(0).max(1_000_000).default(0),
    hardwareMonthlyPlnPerDevice: z.number().min(0).max(1_000_000).default(0),
    mdmMonthlyPlnPerDevice: z.number().min(0).max(1_000_000).default(0),
    supportMonthlyPlnPerDevice: z.number().min(0).max(1_000_000).default(0),
    logisticsMonthlyPlnPerDevice: z.number().min(0).max(1_000_000).default(0),
    failureReserveMonthlyPlnPerDevice: z
        .number()
        .min(0)
        .max(1_000_000)
        .default(0),
    operationalCostsConfirmed: z.boolean().default(false),
});
export const platformCostSettingsResponseSchema = z.object({
    settings: platformCostSettingsSchema,
    revision: z.number().int().min(1),
    canRollback: z.boolean(),
    unpricedEvents: z.number().int().min(0),
    updatedAt: z.iso.datetime(),
    requestId: requestIdSchema,
});
export const platformAiConnectionTestResponseSchema = z.object({
    ok: z.boolean(),
    checkedAt: z.iso.datetime(),
    models: z.object({
        realtime: z.boolean(),
        textVision: z.boolean(),
    }),
    message: z.string().max(500),
    requestId: requestIdSchema,
});
export const schoolLimitsResponseSchema = z.object({
    schoolId: schoolIdSchema,
    source: z.enum(["plan", "school_override"]),
    plan: limitValuesSchema,
    overrides: schoolLimitOverridesSchema,
    effective: limitValuesSchema,
    usage: schoolLimitUsageSchema,
    updatedAt: z.iso.datetime(),
    requestId: requestIdSchema,
});
export const stationLessonStartRequestSchema = z
    .object({
    gradeId: z.string().min(1).max(64),
    schoolSubjectId: z.string().min(1).max(64),
    activityType: learningActivityTypeSchema,
    activityMode: learningActivityModeSchema,
    anonymousSessionId: z
        .string()
        .min(16)
        .max(128)
        .regex(/^[A-Za-z0-9._:-]+$/),
})
    .superRefine((value, context) => {
    if (!isLearningActivityModeForType(value.activityType, value.activityMode)) {
        context.addIssue({
            code: "custom",
            message: "Activity mode does not belong to activity type",
            path: ["activityMode"],
        });
    }
});
export const stationLessonStartResponseSchema = z.object({
    lesson: z.object({
        id: z.string().min(1).max(128),
        status: z.literal("active"),
        activityMode: learningActivityModeSchema,
        assistanceLevel: assistanceLevelSchema,
        maxAssistanceLevel: assistanceLevelSchema,
        startedAt: z.iso.datetime(),
        maxDurationSeconds: z.number().int().min(60).max(86_400),
    }),
    usage: schoolLimitUsageSchema,
    requestId: requestIdSchema,
});
export const stationActiveLessonSchema = z.object({
    id: z.string().min(1).max(128),
    gradeId: z.string().min(1).max(64),
    schoolSubjectId: z.string().min(1).max(64),
    activityType: learningActivityTypeSchema,
    activityMode: learningActivityModeSchema,
    assistanceLevel: assistanceLevelSchema,
    maxAssistanceLevel: assistanceLevelSchema,
    topicSummary: z.string().trim().min(2).max(200).nullable().default(null),
    startedAt: z.iso.datetime(),
    maxDurationSeconds: z.number().int().min(60).max(86_400),
});
export const stationLessonModeRequestSchema = z.object({
    activityMode: learningActivityModeSchema,
    topicSummary: z.string().trim().min(2).max(200).nullable().optional(),
});
export const stationLessonModeResponseSchema = z.object({
    lessonId: z.string().min(1).max(128),
    activityMode: learningActivityModeSchema,
    topicSummary: z.string().trim().min(2).max(200).nullable().default(null),
    requestId: requestIdSchema,
});
export const stationLessonAssistanceLevelRequestSchema = z.object({
    assistanceLevel: assistanceLevelSchema,
});
export const stationLessonAssistanceLevelResponseSchema = z.object({
    lessonId: z.string().min(1).max(128),
    assistanceLevel: assistanceLevelSchema,
    maxAssistanceLevel: assistanceLevelSchema,
    requestId: requestIdSchema,
});
export const stationAssistanceSurfaceSchema = z.enum([
    "chat",
    "quiz",
    "material",
    "whiteboard",
    "voice",
    "photo",
]);
export const stationAssistanceSignalSchema = z.enum([
    "idle_after_attempt",
    "incorrect_answer",
    "repeated_revision",
    "explicit_request",
]);
export const stationAssistanceEvaluateRequestSchema = z
    .object({
    clientEventId: z
        .string()
        .min(8)
        .max(128)
        .regex(/^[A-Za-z0-9._:-]+$/),
    surface: stationAssistanceSurfaceSchema,
    surfaceKey: z.string().trim().min(1).max(240),
    signal: stationAssistanceSignalSchema,
    signalCount: z.number().int().min(1).max(20),
    activeSeconds: z.number().int().min(0).max(86_400),
    dismissedCount: z.number().int().min(0).max(20),
    interventionCount: z.number().int().min(0).max(20),
    quizQuestionId: z
        .string()
        .min(8)
        .max(128)
        .regex(/^[A-Za-z0-9._:-]+$/)
        .optional(),
})
    .superRefine((value, context) => {
    if (value.signal === "incorrect_answer" &&
        (!value.quizQuestionId || value.surface !== "quiz")) {
        context.addIssue({
            code: "custom",
            message: "Incorrect quiz signals require a quiz question",
            path: ["quizQuestionId"],
        });
    }
});
export const stationAssistanceEvaluateResponseSchema = z.object({
    assistanceLevel: assistanceLevelSchema,
    decision: z.enum(["none", "offer", "hint"]),
    reasonCode: stationAssistanceSignalSchema,
    cooldownSeconds: z.number().int().min(0).max(3600),
    requestId: requestIdSchema,
});
const stationRealtimeMaterialContextSchema = z.object({
    materialId: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,63}$/),
    version: z.number().int().positive().max(1_000_000),
    pageNumber: z.number().int().positive().max(10_000),
    selectionText: z.string().trim().min(1).max(1200).optional(),
});
export const stationRealtimeContextRequestSchema = z
    .object({
    surface: stationAssistanceSurfaceSchema,
    materialContext: stationRealtimeMaterialContextSchema.optional(),
    whiteboardContext: z
        .object({
        sceneId: z
            .string()
            .min(1)
            .max(128)
            .regex(/^[A-Za-z0-9._:-]+$/)
            .nullable(),
        revision: z.number().int().min(0).max(1_000_000),
        hasStudentWork: z.boolean(),
    })
        .optional(),
    quizQuestionId: z
        .string()
        .min(8)
        .max(128)
        .regex(/^[A-Za-z0-9._:-]+$/)
        .optional(),
})
    .superRefine((value, context) => {
    if (value.surface === "material" && !value.materialContext) {
        context.addIssue({
            code: "custom",
            message: "Material surface requires material context",
            path: ["materialContext"],
        });
    }
    if (value.surface === "quiz" && !value.quizQuestionId) {
        context.addIssue({
            code: "custom",
            message: "Quiz surface requires a question identifier",
            path: ["quizQuestionId"],
        });
    }
});
export const stationRealtimeContextResponseSchema = z.object({
    instructions: z.string().min(1).max(32_000),
    contextVersion: z.string().min(8).max(128),
    requestId: requestIdSchema,
});
export const stationActiveLessonResponseSchema = z.object({
    lesson: stationActiveLessonSchema.nullable(),
    requestId: requestIdSchema,
});
export const stationLessonMaterialSchema = z.object({
    id: z.string().min(1).max(64),
    title: z.string().min(1).max(160),
    originalFilename: z.string().min(1).max(180),
    mediaType: z.literal("application/pdf"),
    sizeBytes: z
        .number()
        .int()
        .positive()
        .max(20 * 1024 * 1024),
    version: z.number().int().positive(),
});
export const stationLessonMaterialsResponseSchema = z.object({
    lessonId: z.string().min(1).max(128),
    materials: z.array(stationLessonMaterialSchema).max(100),
    requestId: requestIdSchema,
});
export const stationRealtimeClientSecretResponseSchema = z.object({
    clientSecret: z.string().min(16).max(512),
    expiresAt: z.iso.datetime(),
    model: z.string().min(3).max(120),
    moderationEnabled: z.boolean(),
    transport: z.literal("webrtc"),
    requestId: requestIdSchema,
});
export const stationRealtimeTranscriptRequestSchema = z.object({
    providerItemId: z
        .string()
        .min(8)
        .max(128)
        .regex(/^[A-Za-z0-9._:-]+$/),
    role: z.literal("assistant"),
    transcript: z.string().trim().min(1).max(12_000),
});
export const stationRealtimeModerationRequestSchema = z.object({
    providerItemId: z
        .string()
        .min(8)
        .max(128)
        .regex(/^[A-Za-z0-9._:-]+$/),
    transcript: z.string().trim().min(1).max(4000),
});
export const stationRealtimeModerationResponseSchema = z.object({
    action: z.enum(["allow", "block"]),
    reply: z.string().min(1).max(1000).nullable(),
    teacherAlerted: z.boolean(),
    requestId: requestIdSchema,
});
export const stationConversationMessageSchema = z.object({
    role: z.enum(["user", "assistant"]),
    text: z.string().trim().min(1).max(2000),
});
export const stationTextMessageRequestSchema = z
    .object({
    clientMessageId: z
        .string()
        .min(8)
        .max(128)
        .regex(/^[A-Za-z0-9._:-]+$/),
    message: z.string().trim().min(1).max(4000),
    history: z.array(stationConversationMessageSchema).max(8).default([]),
    whiteboardOpen: z.boolean().default(false),
    whiteboardContext: z
        .object({
        sceneId: z
            .string()
            .min(1)
            .max(128)
            .regex(/^[A-Za-z0-9._:-]+$/)
            .nullable(),
        revision: z.number().int().min(0).max(1_000_000),
    })
        .optional(),
    materialContext: z
        .object({
        materialId: z
            .string()
            .min(1)
            .max(64)
            .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,63}$/),
        version: z.number().int().positive().max(1_000_000),
        pageNumber: z.number().int().positive().max(10_000),
        open: z.boolean().default(true),
        selection: z
            .object({
            pageNumber: z.number().int().positive().max(10_000),
            text: z.string().trim().min(1).max(1200),
            rects: z
                .array(z.object({
                x: z.number().min(0).max(1),
                y: z.number().min(0).max(1),
                width: z.number().min(0.001).max(1),
                height: z.number().min(0.001).max(1),
            }))
                .min(1)
                .max(12),
        })
            .optional(),
    })
        .optional(),
})
    .superRefine((value, context) => {
    if (!value.whiteboardOpen && value.whiteboardContext) {
        context.addIssue({
            code: "custom",
            message: "Closed whiteboard requests cannot include board context",
            path: ["whiteboardContext"],
        });
    }
    if (value.materialContext?.selection &&
        value.materialContext.selection.pageNumber !==
            value.materialContext.pageNumber) {
        context.addIssue({
            code: "custom",
            message: "Material selection must belong to the open page",
            path: ["materialContext", "selection", "pageNumber"],
        });
    }
    const totalCharacters = value.history.reduce((sum, message) => sum + message.text.length, value.message.length);
    if (totalCharacters > 10_000) {
        context.addIssue({
            code: "custom",
            message: "Conversation context is too long",
            path: ["history"],
        });
    }
});
const stationWhiteboardCoordinateSchema = z.number().min(0).max(1);
const stationWhiteboardExtentSchema = z.number().min(0.01).max(1);
const stationWhiteboardElementColorSchema = z.enum([
    "primary",
    "warning",
    "success",
    "neutral",
]);
const stationWhiteboardElementIdSchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9._:-]+$/);
const stationWhiteboardBoxSchema = {
    x: stationWhiteboardCoordinateSchema,
    y: stationWhiteboardCoordinateSchema,
    width: stationWhiteboardExtentSchema,
    height: stationWhiteboardExtentSchema,
};
const stationWhiteboardTextElementDraftSchema = z.object({
    kind: z.literal("text"),
    text: z.string().trim().min(1).max(240),
    ...stationWhiteboardBoxSchema,
    color: stationWhiteboardElementColorSchema,
});
const stationWhiteboardFormulaElementDraftSchema = z.object({
    kind: z.literal("formula"),
    latex: z.string().trim().min(1).max(240),
    spokenText: z.string().trim().min(1).max(320),
    ...stationWhiteboardBoxSchema,
    color: stationWhiteboardElementColorSchema,
});
const stationWhiteboardHighlightElementDraftSchema = z.object({
    kind: z.literal("highlight"),
    ...stationWhiteboardBoxSchema,
    color: stationWhiteboardElementColorSchema,
});
const stationWhiteboardPathFields = {
    startX: stationWhiteboardCoordinateSchema,
    startY: stationWhiteboardCoordinateSchema,
    endX: stationWhiteboardCoordinateSchema,
    endY: stationWhiteboardCoordinateSchema,
    strokeWidth: z.number().min(0.001).max(0.03),
    color: stationWhiteboardElementColorSchema,
};
const stationWhiteboardLineElementDraftSchema = z.object({
    kind: z.literal("line"),
    ...stationWhiteboardPathFields,
});
const stationWhiteboardArrowElementDraftSchema = z.object({
    kind: z.literal("arrow"),
    ...stationWhiteboardPathFields,
});
const stationWhiteboardShapeFields = {
    ...stationWhiteboardBoxSchema,
    strokeWidth: z.number().min(0.001).max(0.03),
    color: stationWhiteboardElementColorSchema,
};
const stationWhiteboardRectangleElementDraftSchema = z.object({
    kind: z.literal("rectangle"),
    ...stationWhiteboardShapeFields,
});
const stationWhiteboardEllipseElementDraftSchema = z.object({
    kind: z.literal("ellipse"),
    ...stationWhiteboardShapeFields,
});
export const stationWhiteboardElementDraftSchema = z.discriminatedUnion("kind", [
    stationWhiteboardTextElementDraftSchema,
    stationWhiteboardFormulaElementDraftSchema,
    stationWhiteboardHighlightElementDraftSchema,
    stationWhiteboardLineElementDraftSchema,
    stationWhiteboardArrowElementDraftSchema,
    stationWhiteboardRectangleElementDraftSchema,
    stationWhiteboardEllipseElementDraftSchema,
]);
export const stationWhiteboardElementSchema = z.discriminatedUnion("kind", [
    stationWhiteboardTextElementDraftSchema.extend({
        id: stationWhiteboardElementIdSchema,
    }),
    stationWhiteboardFormulaElementDraftSchema.extend({
        id: stationWhiteboardElementIdSchema,
    }),
    stationWhiteboardHighlightElementDraftSchema.extend({
        id: stationWhiteboardElementIdSchema,
    }),
    stationWhiteboardLineElementDraftSchema.extend({
        id: stationWhiteboardElementIdSchema,
    }),
    stationWhiteboardArrowElementDraftSchema.extend({
        id: stationWhiteboardElementIdSchema,
    }),
    stationWhiteboardRectangleElementDraftSchema.extend({
        id: stationWhiteboardElementIdSchema,
    }),
    stationWhiteboardEllipseElementDraftSchema.extend({
        id: stationWhiteboardElementIdSchema,
    }),
]);
export const stationWhiteboardUpdateDraftSchema = z
    .object({
    operation: z.enum(["none", "replace_base", "append_hint"]),
    autoOpen: z.boolean(),
    elements: z.array(stationWhiteboardElementDraftSchema).max(12),
})
    .superRefine((value, context) => {
    value.elements.forEach((element, index) => {
        if ("width" in element &&
            (element.x + element.width > 1 || element.y + element.height > 1)) {
            context.addIssue({
                code: "custom",
                message: "A whiteboard element must fit inside the board",
                path: ["elements", index],
            });
        }
    });
    if (value.operation === "none" && value.elements.length > 0) {
        context.addIssue({
            code: "custom",
            message: "A no-op whiteboard update cannot contain elements",
            path: ["elements"],
        });
    }
    if (value.operation !== "none" && value.elements.length === 0) {
        context.addIssue({
            code: "custom",
            message: "A whiteboard update must contain an element",
            path: ["elements"],
        });
    }
    if (value.operation === "append_hint" && value.elements.length > 4) {
        context.addIssue({
            code: "custom",
            message: "A whiteboard hint may contain at most four elements",
            path: ["elements"],
        });
    }
});
export const stationWhiteboardUpdateSchema = z
    .object({
    operation: z.enum(["none", "replace_base", "append_hint"]),
    sceneId: stationWhiteboardElementIdSchema.nullable(),
    revision: z.number().int().min(0).max(1_000_000).nullable(),
    autoOpen: z.boolean(),
    elements: z.array(stationWhiteboardElementSchema).max(12),
})
    .superRefine((value, context) => {
    value.elements.forEach((element, index) => {
        if ("width" in element &&
            (element.x + element.width > 1 || element.y + element.height > 1)) {
            context.addIssue({
                code: "custom",
                message: "A whiteboard element must fit inside the board",
                path: ["elements", index],
            });
        }
    });
    if (value.operation === "none") {
        if (value.sceneId !== null ||
            value.revision !== null ||
            value.autoOpen ||
            value.elements.length > 0) {
            context.addIssue({
                code: "custom",
                message: "A no-op whiteboard update must be empty",
                path: ["operation"],
            });
        }
        return;
    }
    if (value.sceneId === null || value.revision === null) {
        context.addIssue({
            code: "custom",
            message: "A whiteboard update requires scene identity",
            path: ["sceneId"],
        });
    }
    if (value.elements.length === 0) {
        context.addIssue({
            code: "custom",
            message: "A whiteboard update must contain an element",
            path: ["elements"],
        });
    }
    if (value.operation === "replace_base" && value.revision === 0) {
        context.addIssue({
            code: "custom",
            message: "A protected base scene requires a positive revision",
            path: ["revision"],
        });
    }
    if (value.operation === "append_hint" && value.elements.length > 4) {
        context.addIssue({
            code: "custom",
            message: "A whiteboard hint may contain at most four elements",
            path: ["elements"],
        });
    }
});
export const stationTutorModelReplyContentSchema = z.object({
    reply: z.string().trim().min(1).max(12_000),
    topicSummary: z.string().trim().min(1).max(120),
    suggestedAction: z.enum(["test_preparation"]).nullable().default(null),
    whiteboardUpdate: stationWhiteboardUpdateDraftSchema,
});
export const stationTutorReplyContentSchema = z.object({
    reply: z.string().trim().min(1).max(12_000),
    topicSummary: z.string().trim().min(1).max(120),
    suggestedAction: z.enum(["test_preparation"]).nullable().default(null),
    whiteboardUpdate: stationWhiteboardUpdateSchema.default({
        operation: "none",
        sceneId: null,
        revision: null,
        autoOpen: false,
        elements: [],
    }),
});
export const stationTextMessageResponseSchema = stationTutorReplyContentSchema.extend({
    model: z.string().min(3).max(120),
    usage: z.object({
        inputTokens: z.number().int().min(0),
        outputTokens: z.number().int().min(0),
        totalTokens: z.number().int().min(0),
    }),
    requestId: requestIdSchema,
});
export const stationQuizQuestionContentSchema = z
    .object({
    prompt: z.string().trim().min(4).max(500),
    options: z.array(z.string().trim().min(1).max(160)).length(4),
    correctIndex: z.number().int().min(0).max(3),
    explanation: z.string().trim().min(4).max(600),
    topicSummary: z.string().trim().min(1).max(120),
})
    .superRefine((value, context) => {
    if (new Set(value.options.map((option) => option.toLowerCase())).size < 4) {
        context.addIssue({
            code: "custom",
            message: "Quiz options must be unique",
            path: ["options"],
        });
    }
});
export const stationQuizQuestionRequestSchema = z.object({
    clientQuestionId: z
        .string()
        .min(8)
        .max(128)
        .regex(/^[A-Za-z0-9._:-]+$/),
    topicScope: z.string().trim().min(2).max(200),
});
export const stationQuizQuestionSchema = z.object({
    id: z.string().min(1).max(128),
    prompt: stationQuizQuestionContentSchema.shape.prompt,
    options: stationQuizQuestionContentSchema.shape.options,
    topicSummary: stationQuizQuestionContentSchema.shape.topicSummary,
    position: z.number().int().min(1).max(5),
    total: z.literal(5),
});
export const stationQuizQuestionResponseSchema = z.object({
    status: z.enum(["active", "completed"]),
    question: stationQuizQuestionSchema.nullable(),
    requestId: requestIdSchema,
});
export const stationQuizAnswerRequestSchema = z.object({
    selectedIndex: z.number().int().min(0).max(3),
});
export const stationQuizAnswerResponseSchema = z.object({
    questionId: z.string().min(1).max(128),
    selectedIndex: z.number().int().min(0).max(3),
    correctIndex: z.number().int().min(0).max(3),
    correct: z.boolean(),
    explanation: stationQuizQuestionContentSchema.shape.explanation,
    completed: z.boolean(),
    requestId: requestIdSchema,
});
export const stationImageAnalysisResultSchema = z.object({
    readable: z.boolean(),
    title: z.string().trim().min(1).max(120),
    observation: z.string().trim().min(1).max(800),
    hint: z.string().trim().min(1).max(800),
});
export const stationImageAnalysisResponseSchema = z.object({
    analysis: stationImageAnalysisResultSchema,
    model: z.string().min(3).max(120),
    usage: z.object({
        inputTokens: z.number().int().min(0),
        outputTokens: z.number().int().min(0),
        totalTokens: z.number().int().min(0),
    }),
    retention: z.object({
        stored: z.boolean(),
        expiresAt: z.iso.datetime().nullable(),
    }),
    requestId: requestIdSchema,
});
export const stationRealtimeUsageSchema = z.object({
    textInputTokens: z.number().int().min(0).max(100_000_000),
    textCachedInputTokens: z.number().int().min(0).max(100_000_000),
    textOutputTokens: z.number().int().min(0).max(100_000_000),
    audioInputTokens: z.number().int().min(0).max(100_000_000),
    audioCachedInputTokens: z.number().int().min(0).max(100_000_000),
    audioOutputTokens: z.number().int().min(0).max(100_000_000),
});
const emptyStationRealtimeUsage = {
    textInputTokens: 0,
    textCachedInputTokens: 0,
    textOutputTokens: 0,
    audioInputTokens: 0,
    audioCachedInputTokens: 0,
    audioOutputTokens: 0,
};
export const stationLessonCompleteRequestSchema = z
    .object({
    outcome: z.enum(["completed", "abandoned_inactivity"]).default("completed"),
    durationSeconds: z
        .number()
        .int()
        .min(0)
        .max(8 * 60 * 60),
    realtimeSeconds: z
        .number()
        .int()
        .min(0)
        .max(8 * 60 * 60),
    realtimeUsage: stationRealtimeUsageSchema.default(emptyStationRealtimeUsage),
    imageCount: z.number().int().min(0).max(100),
    rating: z.number().int().min(1).max(6).nullable(),
    topicSummary: z.string().trim().max(500).nullable(),
})
    .refine((value) => value.realtimeSeconds <= value.durationSeconds, {
    message: "Realtime usage cannot exceed lesson duration",
    path: ["realtimeSeconds"],
})
    .refine((value) => value.outcome === "completed" || value.rating === null, {
    message: "Abandoned lessons cannot include a rating",
    path: ["rating"],
});
export const stationLessonCompleteResponseSchema = z.object({
    lesson: z.object({
        id: z.string().min(1).max(128),
        status: z.enum(["completed", "abandoned"]),
        endedAt: z.iso.datetime(),
    }),
    usage: schoolLimitUsageSchema,
    requestId: requestIdSchema,
});
export const teacherEscalationStatusSchema = z.enum([
    "open",
    "acknowledged",
    "resolved",
    "dismissed",
]);
export const teacherEscalationReasonSchema = z.enum([
    "student_request",
    "safety_alert",
]);
export const teacherEscalationRoutingStatusSchema = z.enum([
    "assigned",
    "outside_hours",
    "unassigned",
]);
export const teacherEscalationSchema = z.object({
    id: z.string().min(1).max(128),
    lessonId: z.string().min(1).max(128),
    schoolId: schoolIdSchema,
    stationName: z.string().min(1).max(200),
    stationLocation: z.string().max(200).nullable(),
    gradeName: z.string().min(1).max(100),
    subjectName: z.string().min(1).max(100),
    activityType: learningActivityTypeSchema,
    status: teacherEscalationStatusSchema,
    reasonCode: teacherEscalationReasonSchema,
    note: z.string().max(500).nullable(),
    assignedTo: z
        .object({
        id: z.string().min(1).max(128),
        displayName: z.string().min(1).max(200),
    })
        .nullable(),
    routingStatus: teacherEscalationRoutingStatusSchema,
    responseDueAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    acknowledgedAt: z.iso.datetime().nullable(),
    resolvedAt: z.iso.datetime().nullable(),
});
export const schoolTeacherEscalationsResponseSchema = z.object({
    schoolId: schoolIdSchema,
    openCount: z.number().int().min(0),
    escalations: z.array(teacherEscalationSchema),
    requestId: requestIdSchema,
});
export const stationTeacherHelpRequestSchema = z.object({
    note: z.string().trim().max(500).nullable().default(null),
});
export const stationTeacherHelpResponseSchema = z.object({
    escalation: teacherEscalationSchema,
    requestId: requestIdSchema,
});
export const schoolTeacherEscalationUpdateRequestSchema = z.object({
    status: z.enum(["acknowledged", "resolved", "dismissed"]),
});
export const schoolTeacherEscalationMutationResponseSchema = z.object({
    escalation: teacherEscalationSchema,
    requestId: requestIdSchema,
});
export const teacherDutyOutsideHoursModeSchema = z.enum([
    "queue",
    "school_admins",
]);
export const teacherDutyRuleInputSchema = z
    .object({
    userId: z.string().min(1).max(128),
    gradeId: z.string().min(1).max(128).nullable().default(null),
    subjectId: z.string().min(1).max(128).nullable().default(null),
    weekdays: z.array(z.number().int().min(1).max(7)).min(1).max(7),
    startsAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    endsAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    priority: z.number().int().min(1).max(1000).default(100),
    active: z.boolean().default(true),
})
    .refine((value) => value.endsAt > value.startsAt, {
    message: "Duty end must be later than duty start",
    path: ["endsAt"],
});
export const teacherDutyRuleSchema = teacherDutyRuleInputSchema.extend({
    id: z.string().min(1).max(128),
    userDisplayName: z.string().min(1).max(200),
    gradeName: z.string().min(1).max(100).nullable(),
    subjectName: z.string().min(1).max(100).nullable(),
    updatedAt: z.iso.datetime(),
});
export const teacherDutySettingsUpdateRequestSchema = z.object({
    responseTargetMinutes: z.number().int().min(1).max(120),
    outsideHoursMode: teacherDutyOutsideHoursModeSchema,
});
export const teacherDutyResponseSchema = z.object({
    schoolId: schoolIdSchema,
    timezone: z.string().min(1).max(100),
    responseTargetMinutes: z.number().int().min(1).max(120),
    outsideHoursMode: teacherDutyOutsideHoursModeSchema,
    rules: z.array(teacherDutyRuleSchema),
    requestId: requestIdSchema,
});
export const teacherDutyRuleMutationResponseSchema = z.object({
    rule: teacherDutyRuleSchema,
    requestId: requestIdSchema,
});
export const incidentCategorySchema = z.enum([
    "device",
    "technical",
    "ai",
    "content",
    "privacy",
    "billing",
    "other",
]);
export const incidentPrioritySchema = z.enum([
    "low",
    "medium",
    "high",
    "critical",
]);
export const incidentStatusSchema = z.enum([
    "new",
    "in_progress",
    "waiting_school",
    "resolved",
    "closed",
]);
export const incidentSlaStatusSchema = z.enum([
    "on_track",
    "at_risk",
    "met",
    "breached",
]);
const supportSlaTargetSchema = z
    .object({
    responseMinutes: z.number().int().min(1).max(10080),
    resolutionMinutes: z.number().int().min(1).max(43200),
})
    .refine((value) => value.resolutionMinutes >= value.responseMinutes, {
    message: "Resolution target cannot be shorter than response target",
    path: ["resolutionMinutes"],
});
export const supportPolicySchema = z
    .object({
    timezone: z.string().min(1).max(100),
    weekdays: z.array(z.number().int().min(1).max(7)).min(1).max(7),
    startsAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    endsAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    critical24x7: z.boolean(),
    targets: z.object({
        critical: supportSlaTargetSchema,
        high: supportSlaTargetSchema,
        medium: supportSlaTargetSchema,
        low: supportSlaTargetSchema,
    }),
})
    .refine((value) => value.endsAt > value.startsAt, {
    message: "Support end must be later than support start",
    path: ["endsAt"],
});
export const platformSupportPolicyResponseSchema = supportPolicySchema.extend({
    updatedAt: z.iso.datetime(),
    requestId: requestIdSchema,
});
export const incidentSummarySchema = z.object({
    id: z.string().min(1).max(128),
    publicId: z.string().min(8).max(32),
    schoolId: schoolIdSchema,
    schoolName: z.string().min(1).max(200),
    deviceId: z.string().min(1).max(128).nullable(),
    deviceName: z.string().min(1).max(200).nullable(),
    source: z.enum(["school", "hq", "system"]),
    category: incidentCategorySchema,
    priority: incidentPrioritySchema,
    status: incidentStatusSchema,
    title: z.string().min(4).max(160),
    description: z.string().min(10).max(5000),
    assignedTo: z
        .object({
        id: z.string().min(1).max(128),
        displayName: z.string().min(1).max(200),
        email: z.email(),
    })
        .nullable(),
    slaDueAt: z.iso.datetime(),
    responseDueAt: z.iso.datetime(),
    resolutionDueAt: z.iso.datetime(),
    firstRespondedAt: z.iso.datetime().nullable(),
    responseSlaStatus: incidentSlaStatusSchema,
    resolutionSlaStatus: incidentSlaStatusSchema,
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    resolvedAt: z.iso.datetime().nullable(),
});
export const incidentCommentSchema = z.object({
    id: z.string().min(1).max(128),
    authorType: z.enum(["user", "system"]),
    authorName: z.string().min(1).max(200),
    body: z.string().min(1).max(2000),
    visibility: z.enum(["school", "internal"]),
    createdAt: z.iso.datetime(),
});
export const incidentStatusEventSchema = z.object({
    id: z.string().min(1).max(128),
    actorName: z.string().min(1).max(200),
    fromStatus: incidentStatusSchema.nullable(),
    toStatus: incidentStatusSchema,
    note: z.string().max(500).nullable(),
    createdAt: z.iso.datetime(),
});
export const incidentAttachmentSchema = z.object({
    id: z.string().min(1).max(128),
    originalFilename: z.string().min(1).max(180),
    mediaType: z.enum(["application/pdf", "image/jpeg", "image/png"]),
    sizeBytes: z
        .number()
        .int()
        .min(1)
        .max(10 * 1024 * 1024),
    visibility: z.enum(["school", "internal"]),
    uploadedBy: z.string().min(1).max(200),
    createdAt: z.iso.datetime(),
});
export const incidentsResponseSchema = z.object({
    incidents: z.array(incidentSummarySchema),
    requestId: requestIdSchema,
});
export const hqIncidentsQuerySchema = z.object({
    q: z.string().trim().min(2).max(100).optional(),
    status: z
        .union([incidentStatusSchema, z.literal("active")])
        .default("active"),
    priority: incidentPrioritySchema.optional(),
    schoolId: schoolIdSchema.optional(),
    sort: z.enum(["sla", "updated", "created"]).default("sla"),
    direction: z.enum(["asc", "desc"]).default("asc"),
    cursor: cursorSchema.optional(),
    limit: z.coerce.number().int().min(10).max(100).default(50),
});
export const hqIncidentsResponseSchema = incidentsResponseSchema.extend({
    page: cursorPageSchema,
});
export const incidentDetailsResponseSchema = z.object({
    incident: incidentSummarySchema,
    comments: z.array(incidentCommentSchema),
    attachments: z.array(incidentAttachmentSchema),
    events: z.array(incidentStatusEventSchema),
    requestId: requestIdSchema,
});
export const schoolIncidentCreateRequestSchema = z.object({
    deviceId: z.string().min(1).max(128).nullable(),
    category: incidentCategorySchema.exclude(["billing"]),
    priority: incidentPrioritySchema.extract(["low", "medium", "high"]),
    title: z.string().trim().min(4).max(160),
    description: z.string().trim().min(10).max(5000),
});
export const hqIncidentCreateRequestSchema = schoolIncidentCreateRequestSchema
    .omit({ category: true, priority: true })
    .extend({
    schoolId: schoolIdSchema,
    category: incidentCategorySchema,
    priority: incidentPrioritySchema,
});
export const hqIncidentUpdateRequestSchema = z.object({
    priority: incidentPrioritySchema,
    status: incidentStatusSchema,
    assignedToId: z.string().min(1).max(128).nullable(),
    note: z.string().trim().max(500).nullable(),
});
export const incidentCommentCreateRequestSchema = z.object({
    body: z.string().trim().min(1).max(2000),
    visibility: z.enum(["school", "internal"]),
});
export const dataExportFormatSchema = z.enum(["csv", "pdf"]);
export const schoolDataExportQuerySchema = z.object({
    format: dataExportFormatSchema,
    days: z.coerce
        .number()
        .int()
        .refine((value) => [7, 30, 90].includes(value)),
    gradeId: z.string().min(1).max(128).optional(),
    schoolSubjectId: z.string().min(1).max(128).optional(),
});
export const schoolLessonReportQuerySchema = schoolDataExportQuerySchema.omit({
    format: true,
});
export const schoolLessonReportResponseSchema = z.object({
    schoolId: schoolIdSchema,
    privacy: z.object({
        minimumGroupSize: z.number().int().min(2).max(20),
        ratingsAvailable: z.boolean(),
        suppressedTopicGroups: z.number().int().min(0),
        suppressedActivityGroups: z.number().int().min(0),
    }),
    period: z.object({
        days: z
            .number()
            .int()
            .refine((value) => [7, 30, 90].includes(value)),
        startsAt: z.iso.datetime(),
        endsAt: z.iso.datetime(),
    }),
    summary: z.object({
        lessons: z.number().int().min(0),
        totalDurationSeconds: z.number().int().min(0),
        averageDurationSeconds: z.number().int().min(0),
        ratedLessons: z.number().int().min(0),
        ratingPercent: z.number().min(0).max(100).nullable(),
        averageRating: z.number().min(1).max(6).nullable(),
        realtimeSeconds: z.number().int().min(0),
        imageAnalyses: z.number().int().min(0),
    }),
    timeline: z.array(z.object({
        date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        lessons: z.number().int().min(0),
        durationSeconds: z.number().int().min(0),
        averageRating: z.number().min(1).max(6).nullable(),
    })),
    topics: z.array(z.object({
        topic: z.string().min(1).max(500),
        subjectName: z.string().min(1).max(200),
        gradeName: z.string().min(1).max(100),
        activityType: learningActivityTypeSchema,
        lessons: z.number().int().min(1),
        durationSeconds: z.number().int().min(0),
        averageRating: z.number().min(1).max(6).nullable(),
    })),
    materialSources: z
        .array(z.object({
        materialId: z.string().min(1).max(128),
        title: z.string().min(1).max(160),
        version: z.number().int().positive(),
        lessons: z.number().int().min(5),
    }))
        .optional(),
    activityGroups: z.array(z.object({
        date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        gradeName: z.string().min(1).max(100),
        subjectName: z.string().min(1).max(200),
        activityType: learningActivityTypeSchema,
        topic: z.string().min(1).max(500),
        lessons: z.number().int().min(5),
        durationSeconds: z.number().int().min(0),
        realtimeSeconds: z.number().int().min(0),
        imageAnalyses: z.number().int().min(0),
        averageRating: z.number().min(1).max(6).nullable(),
        teacherHelpRequests: z.number().int().min(0),
    })),
    requestId: requestIdSchema,
});
export const hqDataExportQuerySchema = z.object({
    format: dataExportFormatSchema,
    days: z.coerce
        .number()
        .int()
        .refine((value) => [30, 90, 365].includes(value)),
});
export const hqDataExportTypeSchema = z.enum(["usage", "fleet", "sales"]);
export const schoolReadinessItemKeySchema = z.enum([
    "school_profile",
    "school_administrator",
    "curriculum",
    "paired_station",
    "managed_station",
    "station_connected",
    "pilot_scope",
    "legal_documents",
    "staff_training",
    "student_communication",
    "device_test",
    "learning_materials",
]);
export const schoolReadinessManualItemKeySchema = z.enum([
    "pilot_scope",
    "legal_documents",
    "staff_training",
    "student_communication",
    "device_test",
]);
export const schoolReadinessItemSchema = z.object({
    key: schoolReadinessItemKeySchema,
    title: z.string().min(1).max(200),
    actionTitle: z.string().min(1).max(200),
    description: z.string().min(1).max(500),
    source: z.enum(["automatic", "manual"]),
    required: z.boolean(),
    completed: z.boolean(),
    completedAt: z.iso.datetime().nullable(),
    completedByName: z.string().min(1).max(200).nullable(),
    evidenceNote: z.string().min(8).max(1000).nullable(),
    updatedAt: z.iso.datetime().nullable(),
});
export const schoolReadinessResponseSchema = z.object({
    schoolId: schoolIdSchema,
    items: z.array(schoolReadinessItemSchema),
    completedRequired: z.number().int().min(0),
    requiredCount: z.number().int().positive(),
    progressPercent: z.number().int().min(0).max(100),
    ready: z.boolean(),
    requestId: requestIdSchema,
});
export const schoolReadinessUpdateRequestSchema = z.discriminatedUnion("completed", [
    z.object({
        itemKey: schoolReadinessManualItemKeySchema,
        completed: z.literal(true),
        evidenceNote: z.string().trim().min(8).max(1000),
    }),
    z.object({
        itemKey: schoolReadinessManualItemKeySchema,
        completed: z.literal(false),
        evidenceNote: z.null().optional(),
    }),
]);
export const schoolStatusSchema = z.enum([
    "lead",
    "pilot",
    "active",
    "suspended",
    "closed",
]);
export const schoolPlanCodeSchema = z.enum([
    "pilot",
    "school",
    "pro",
    "custom",
]);
export const hqSchoolCreateRequestSchema = z.object({
    name: z.string().trim().min(2).max(200),
    legalName: z.string().trim().min(2).max(240).nullable(),
    slug: z
        .string()
        .trim()
        .min(2)
        .max(100)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    contactEmail: z.email(),
    status: schoolStatusSchema.extract(["lead", "pilot", "active"]),
    planCode: schoolPlanCodeSchema,
    contractedDeviceCount: z.number().int().min(0).max(1_000),
    timezone: z.string().trim().min(1).max(64),
    locale: z.enum(["pl-PL", "en-GB"]),
    openAi: schoolOpenAiCredentialCreateSchema.nullable().default(null),
});
export const hqSchoolSummarySchema = z.object({
    id: schoolIdSchema,
    slug: z.string().min(1).max(100),
    name: z.string().min(1).max(200),
    contactEmail: z.email(),
    status: schoolStatusSchema,
    planCode: schoolPlanCodeSchema,
    contractedDeviceCount: z.number().int().min(0).max(1_000),
    deviceCount: z.number().int().min(0),
    effectiveMonthlyLessons: limitValuesSchema.shape.monthlyLessons,
    limitSource: z.enum(["plan", "school_override"]),
});
export const hqSchoolsQuerySchema = z.object({
    q: z.string().trim().min(2).max(100).optional(),
    status: schoolStatusSchema.optional(),
    planCode: schoolPlanCodeSchema.optional(),
    sort: z.enum(["name", "status", "plan"]).default("name"),
    direction: z.enum(["asc", "desc"]).default("asc"),
    cursor: cursorSchema.optional(),
    limit: z.coerce.number().int().min(10).max(100).default(50),
});
export const hqSchoolsResponseSchema = z.object({
    schools: z.array(hqSchoolSummarySchema),
    page: cursorPageSchema,
    requestId: requestIdSchema,
});
export const hqSchoolCreateResponseSchema = z.object({
    school: hqSchoolSummarySchema,
    requestId: requestIdSchema,
});
export const hqSchoolAccountOwnerSchema = z.object({
    id: z.string().min(1).max(128),
    displayName: z.string().min(1).max(200),
    email: z.email(),
    status: z.enum(["invited", "active", "suspended"]),
});
export const hqSchoolDetailsSchema = hqSchoolSummarySchema.extend({
    legalName: z.string().min(2).max(240).nullable(),
    timezone: z.string().min(1).max(64),
    locale: z.enum(["pl-PL", "en-GB"]),
    accountOwner: hqTeamMemberSchema.nullable().default(null),
    contractReference: z.string().min(2).max(100).nullable().default(null),
    contractSignedOn: z.iso.date().nullable().default(null),
    contractEndsOn: z.iso.date().nullable().default(null),
    accountOwners: z.array(hqSchoolAccountOwnerSchema),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const hqSchoolDetailsResponseSchema = z.object({
    school: hqSchoolDetailsSchema,
    requestId: requestIdSchema,
});
export const hqSchoolUpdateRequestSchema = hqSchoolCreateRequestSchema
    .omit({ slug: true, status: true, openAi: true })
    .extend({
    status: schoolStatusSchema,
    accountOwnerUserId: z.string().min(1).max(128).nullable().default(null),
    contractReference: z
        .string()
        .trim()
        .min(2)
        .max(100)
        .nullable()
        .default(null),
    contractSignedOn: z.iso.date().nullable().default(null),
    contractEndsOn: z.iso.date().nullable().default(null),
})
    .refine((value) => !value.contractSignedOn ||
    !value.contractEndsOn ||
    value.contractEndsOn >= value.contractSignedOn, {
    message: "Contract end date cannot precede signing date",
    path: ["contractEndsOn"],
});
export const hqDashboardResponseSchema = z.object({
    activeSchools: z.number().int().min(0),
    pilotSchools: z.number().int().min(0),
    devices: z.object({
        total: z.number().int().min(0),
        online: z.number().int().min(0),
        attention: z.number().int().min(0),
        offlineOver24Hours: z.number().int().min(0),
    }),
    usage: z.object({
        lessonsThisMonth: z.number().int().min(0),
        costMicrousdThisMonth: z.number().int().min(0),
        schoolsNearLessonLimit: z.number().int().min(0),
    }),
    openEscalations: z.number().int().min(0),
    generatedAt: z.iso.datetime(),
    requestId: requestIdSchema,
});
export const salesLeadStatusSchema = z.enum([
    "new",
    "qualified",
    "demo",
    "proposal",
    "pilot",
    "negotiation",
    "won",
    "lost",
]);
export const salesLeadSourceSchema = z.enum([
    "landing",
    "referral",
    "outbound",
    "event",
    "manual",
]);
export const leadReferrerCategorySchema = z.enum([
    "direct",
    "internal",
    "search",
    "social",
    "external",
]);
const leadCampaignValueSchema = z
    .string()
    .trim()
    .min(1)
    .max(80)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/);
const leadLandingPathSchema = z
    .string()
    .trim()
    .min(1)
    .max(160)
    .regex(/^\/[A-Za-z0-9/_.-]*$/)
    .refine((value) => !value.includes(".."));
export const hqSalesLeadSchema = z.object({
    id: z.string().min(1).max(128),
    organizationName: z.string().min(2).max(240),
    contactName: z.string().min(2).max(200),
    contactEmail: z.email(),
    contactRole: z.string().min(1).max(120).nullable(),
    source: salesLeadSourceSchema,
    status: salesLeadStatusSchema,
    estimatedDevices: z.number().int().min(1).max(1000),
    estimatedArrPln: z.number().int().min(0).max(100_000_000),
    message: z.string().max(4000).nullable(),
    nextAction: z.string().max(500).nullable(),
    nextActionAt: z.iso.datetime().nullable(),
    ownerName: z.string().min(1).max(200).nullable(),
    lostReason: z.string().max(500).nullable(),
    landingPath: leadLandingPathSchema.nullable().default(null),
    campaignSource: leadCampaignValueSchema.nullable().default(null),
    campaignMedium: leadCampaignValueSchema.nullable().default(null),
    campaignName: leadCampaignValueSchema.nullable().default(null),
    referrerCategory: leadReferrerCategorySchema.nullable().default(null),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const hqSalesLeadsQuerySchema = z.object({
    q: z.string().trim().min(2).max(100).optional(),
    status: salesLeadStatusSchema.optional(),
    source: salesLeadSourceSchema.optional(),
    sort: z.enum(["updated", "created", "organization"]).default("updated"),
    direction: z.enum(["asc", "desc"]).default("desc"),
    cursor: cursorSchema.optional(),
    limit: z.coerce.number().int().min(10).max(100).default(50),
});
export const hqSalesLeadsResponseSchema = z.object({
    leads: z.array(hqSalesLeadSchema),
    page: cursorPageSchema,
    requestId: requestIdSchema,
});
export const hqSalesActivitySchema = z.object({
    id: z.string().min(1).max(128),
    type: z.enum([
        "created",
        "stage_changed",
        "note",
        "email",
        "call",
        "meeting",
        "task_completed",
    ]),
    body: z.string().min(1).max(2000),
    actorName: z.string().min(1).max(200).nullable(),
    createdAt: z.iso.datetime(),
});
export const hqSalesTaskSchema = z.object({
    id: z.string().min(1).max(128),
    leadId: z.string().min(1).max(128),
    title: z.string().min(1).max(500),
    dueAt: z.iso.datetime().nullable(),
    status: z.enum(["open", "completed"]),
    ownerName: z.string().min(1).max(200).nullable(),
    createdAt: z.iso.datetime(),
    completedAt: z.iso.datetime().nullable(),
});
export const hqSalesInteractionSchema = z.object({
    id: z.string().min(1).max(128),
    leadId: z.string().min(1).max(128),
    kind: z.enum(["email", "call", "meeting", "demo"]),
    summary: z.string().min(2).max(2000),
    happenedAt: z.iso.datetime(),
    actorName: z.string().min(1).max(200).nullable(),
    createdAt: z.iso.datetime(),
});
export const hqSalesDocumentSchema = z.object({
    id: z.string().min(1).max(128),
    leadId: z.string().min(1).max(128),
    kind: z.enum(["proposal", "contract"]),
    title: z.string().min(2).max(240),
    amountPln: z.number().int().min(0).max(100_000_000),
    status: z.enum(["draft", "sent", "accepted", "rejected"]),
    validUntil: z.iso.date().nullable(),
    externalUrl: z.url().nullable(),
    ownerName: z.string().min(1).max(200).nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const hqSalesLeadEmailDeliverySchema = z.object({
    status: z.enum(["pending", "sent", "failed", "skipped"]),
    attemptCount: z.number().int().min(0),
    errorCode: z.string().min(1).max(100).nullable(),
    updatedAt: z.iso.datetime(),
});
export const hqSalesLeadDetailsResponseSchema = z.object({
    lead: hqSalesLeadSchema,
    activities: z.array(hqSalesActivitySchema),
    tasks: z.array(hqSalesTaskSchema).default([]),
    interactions: z.array(hqSalesInteractionSchema).default([]),
    documents: z.array(hqSalesDocumentSchema).default([]),
    confirmationEmail: hqSalesLeadEmailDeliverySchema.nullable().default(null),
    conversion: z
        .object({
        schoolId: schoolIdSchema,
        schoolName: z.string().min(1).max(200),
        convertedAt: z.iso.datetime(),
    })
        .nullable()
        .default(null),
    requestId: requestIdSchema,
});
export const hqSalesLeadCreateRequestSchema = z.object({
    organizationName: z.string().trim().min(2).max(240),
    contactName: z.string().trim().min(2).max(200),
    contactEmail: z.string().trim().toLowerCase().pipe(z.email()),
    contactRole: z.string().trim().min(1).max(120).nullable(),
    source: salesLeadSourceSchema.default("manual"),
    estimatedDevices: z.number().int().min(1).max(1000),
    estimatedArrPln: z.number().int().min(0).max(100_000_000),
    message: z.string().trim().max(4000).nullable(),
    nextAction: z.string().trim().max(500).nullable(),
    nextActionAt: z.iso.datetime().nullable(),
});
export const hqSalesLeadUpdateRequestSchema = z
    .object({
    status: salesLeadStatusSchema,
    estimatedDevices: z.number().int().min(1).max(1000),
    estimatedArrPln: z.number().int().min(0).max(100_000_000),
    nextAction: z.string().trim().max(500).nullable(),
    nextActionAt: z.iso.datetime().nullable(),
    lostReason: z.string().trim().max(500).nullable(),
    activityNote: z.string().trim().max(2000).nullable(),
})
    .refine((value) => value.status !== "lost" || Boolean(value.lostReason), {
    message: "Lost reason is required",
    path: ["lostReason"],
});
export const hqSalesLeadMutationResponseSchema = z.object({
    lead: hqSalesLeadSchema,
    requestId: requestIdSchema,
});
export const hqSalesLeadConvertRequestSchema = z.object({
    legalName: z.string().trim().min(2).max(240).nullable(),
    slug: z
        .string()
        .trim()
        .min(2)
        .max(100)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    status: schoolStatusSchema.extract(["pilot", "active"]),
    planCode: schoolPlanCodeSchema,
    contractedDeviceCount: z.number().int().min(1).max(1000),
    timezone: z.string().trim().min(1).max(64),
    locale: z.enum(["pl-PL", "en-GB"]),
});
export const hqSalesLeadConvertResponseSchema = z.object({
    school: hqSchoolSummarySchema,
    reused: z.boolean(),
    requestId: requestIdSchema,
});
export const hqSalesTaskCreateRequestSchema = z.object({
    title: z.string().trim().min(2).max(500),
    dueAt: z.iso.datetime().nullable(),
});
export const hqSalesTaskUpdateRequestSchema = z.object({
    status: z.enum(["open", "completed"]),
});
export const hqSalesTaskMutationResponseSchema = z.object({
    task: hqSalesTaskSchema,
    requestId: requestIdSchema,
});
export const hqSalesInteractionCreateRequestSchema = z.object({
    kind: z.enum(["email", "call", "meeting", "demo"]),
    summary: z.string().trim().min(2).max(2000),
    happenedAt: z.iso.datetime(),
});
export const hqSalesInteractionMutationResponseSchema = z.object({
    interaction: hqSalesInteractionSchema,
    requestId: requestIdSchema,
});
export const hqSalesDocumentCreateRequestSchema = z.object({
    kind: z.enum(["proposal", "contract"]),
    title: z.string().trim().min(2).max(240),
    amountPln: z.number().int().min(0).max(100_000_000),
    status: z.enum(["draft", "sent", "accepted", "rejected"]),
    validUntil: z.iso.date().nullable(),
    externalUrl: z.url().nullable(),
});
export const hqSalesDocumentUpdateRequestSchema = z.object({
    status: z.enum(["draft", "sent", "accepted", "rejected"]),
});
export const hqSalesDocumentMutationResponseSchema = z.object({
    document: hqSalesDocumentSchema,
    requestId: requestIdSchema,
});
export const publicLeadCreateRequestSchema = z.object({
    organizationName: z.string().trim().min(2).max(240),
    contactName: z.string().trim().min(2).max(200),
    contactEmail: z.string().trim().toLowerCase().pipe(z.email()),
    contactRole: z.string().trim().min(1).max(120).nullable(),
    estimatedDevices: z.number().int().min(1).max(1000).default(1),
    message: z.string().trim().max(4000).nullable(),
    consent: z.literal(true),
    website: z.string().max(200).default(""),
    turnstileToken: z.string().trim().min(1).max(2048),
    landingPath: leadLandingPathSchema.nullable().default(null),
    campaignSource: leadCampaignValueSchema.nullable().default(null),
    campaignMedium: leadCampaignValueSchema.nullable().default(null),
    campaignName: leadCampaignValueSchema.nullable().default(null),
    referrerCategory: leadReferrerCategorySchema.nullable().default(null),
});
export const publicLeadCreateResponseSchema = z.object({
    accepted: z.literal(true),
    requestId: requestIdSchema,
});
export const pilotStatusSchema = z.enum([
    "planned",
    "preparing",
    "active",
    "review",
    "completed",
    "cancelled",
]);
export const pilotStatusTransitions = {
    planned: ["preparing", "cancelled"],
    preparing: ["planned", "active", "cancelled"],
    active: ["review", "cancelled"],
    review: ["active", "cancelled"],
    completed: [],
    cancelled: [],
};
export function canTransitionPilotStatus(current, next) {
    return ((current === next && current !== "completed" && current !== "cancelled") ||
        pilotStatusTransitions[current].includes(next));
}
export const hqPilotSchema = z.object({
    id: z.string().min(1).max(128),
    schoolId: z.string().min(1).max(128).nullable(),
    schoolName: z.string().min(1).max(240).nullable(),
    leadId: z.string().min(1).max(128).nullable(),
    leadName: z.string().min(1).max(240).nullable(),
    name: z.string().min(2).max(240),
    status: pilotStatusSchema,
    startsOn: z.iso.date(),
    endsOn: z.iso.date(),
    targetDevices: z.number().int().min(1).max(1000),
    deploymentReadyDevices: z.number().int().min(0),
    scope: z.string().min(2).max(4000),
    successCriteria: z.string().max(4000).nullable(),
    ownerName: z.string().min(1).max(200).nullable(),
    nextAction: z.string().max(500).nullable(),
    nextActionAt: z.iso.datetime().nullable(),
    lessonCount: z.number().int().min(0),
    averageRating: z.number().min(1).max(6).nullable(),
    dailyReview: z.object({
        latestDate: z.iso.date().nullable(),
        firstWeekExpected: z.number().int().min(0).max(7),
        firstWeekCompleted: z.number().int().min(0).max(7),
        firstWeekMissing: z.number().int().min(0).max(7),
    }),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const hqPilotsResponseSchema = z.object({
    pilots: z.array(hqPilotSchema),
    requestId: requestIdSchema,
});
export const hqPilotCreateRequestSchema = z
    .object({
    schoolId: z.string().min(1).max(128).nullable(),
    leadId: z.string().min(1).max(128).nullable(),
    name: z.string().trim().min(2).max(240),
    startsOn: z.iso.date(),
    endsOn: z.iso.date(),
    targetDevices: z.number().int().min(1).max(1000),
    scope: z.string().trim().min(2).max(4000),
    successCriteria: z.string().trim().min(10).max(4000),
    nextAction: z.string().trim().max(500).nullable(),
    nextActionAt: z.iso.datetime().nullable(),
})
    .refine((value) => Boolean(value.schoolId) !== Boolean(value.leadId), {
    message: "Exactly one pilot target is required",
    path: ["schoolId"],
})
    .refine((value) => value.startsOn <= value.endsOn, {
    message: "Pilot end date must not precede its start date",
    path: ["endsOn"],
});
export const hqPilotUpdateRequestSchema = z
    .object({
    status: pilotStatusSchema,
    startsOn: z.iso.date(),
    endsOn: z.iso.date(),
    targetDevices: z.number().int().min(1).max(1000),
    scope: z.string().trim().min(2).max(4000),
    successCriteria: z.string().trim().min(10).max(4000).nullable(),
    nextAction: z.string().trim().max(500).nullable(),
    nextActionAt: z.iso.datetime().nullable(),
})
    .refine((value) => value.startsOn <= value.endsOn, {
    message: "Pilot end date must not precede its start date",
    path: ["endsOn"],
});
export const hqPilotMutationResponseSchema = z.object({
    pilot: hqPilotSchema,
    requestId: requestIdSchema,
});
export const pilotDailyReviewDecisionSchema = z.enum([
    "continue",
    "continue_with_actions",
    "pause",
]);
export const hqPilotDailyReviewSchema = z.object({
    id: z.string().min(1).max(128),
    pilotId: z.string().min(1).max(128),
    schoolId: schoolIdSchema,
    reviewDate: z.iso.date(),
    decision: pilotDailyReviewDecisionSchema,
    lessonCount: z.number().int().min(0),
    averageRating: z.number().min(1).max(6).nullable(),
    openEscalationCount: z.number().int().min(0),
    openIncidentCount: z.number().int().min(0),
    offlineDeviceCount: z.number().int().min(0),
    directCostMicrousd: z.number().int().min(0),
    unpricedCostEventCount: z.number().int().min(0),
    nextAction: z.string().min(2).max(500).nullable(),
    nextActionAt: z.iso.datetime().nullable(),
    reviewerName: z.string().min(1).max(200),
    reviewedAt: z.iso.datetime(),
});
export const hqPilotDailyReviewsResponseSchema = z.object({
    reviews: z.array(hqPilotDailyReviewSchema),
    requestId: requestIdSchema,
});
export const hqPilotDailyReviewCreateRequestSchema = z
    .object({
    reviewDate: z.iso.date(),
    safetyReviewed: z.literal(true),
    privacyReviewed: z.literal(true),
    fleetReviewed: z.literal(true),
    costReviewed: z.literal(true),
    learningQualityReviewed: z.literal(true),
    decision: pilotDailyReviewDecisionSchema,
    nextAction: z.string().trim().min(2).max(500).nullable(),
    nextActionAt: z.iso.datetime().nullable(),
})
    .superRefine((value, context) => {
    if (value.decision !== "continue" && !value.nextAction) {
        context.addIssue({
            code: "custom",
            message: "A follow-up action is required for this decision",
            path: ["nextAction"],
        });
    }
});
export const hqPilotDailyReviewMutationResponseSchema = z.object({
    review: hqPilotDailyReviewSchema,
    requestId: requestIdSchema,
});
export const pilotFeedbackRespondentGroupSchema = z.enum([
    "students",
    "teachers",
    "school_admins",
]);
export const pilotFeedbackSourceSchema = z.enum([
    "survey",
    "interview",
    "observation",
]);
export const pilotFeedbackIssueCountsSchema = z.object({
    answerGiven: z.number().int().min(0).max(1000),
    incorrect: z.number().int().min(0).max(1000),
    unclear: z.number().int().min(0).max(1000),
    technical: z.number().int().min(0).max(1000),
    safety: z.number().int().min(0).max(1000),
    privacy: z.number().int().min(0).max(1000),
});
export const hqPilotFeedbackSummarySchema = z.object({
    id: z.string().min(1).max(128),
    pilotId: z.string().min(1).max(128),
    schoolId: schoolIdSchema,
    collectedOn: z.iso.date(),
    respondentGroup: pilotFeedbackRespondentGroupSchema,
    source: pilotFeedbackSourceSchema,
    sampleSize: z.number().int().min(1).max(1000),
    usefulnessRating: z.number().min(1).max(6),
    understandingRating: z.number().min(1).max(6).nullable(),
    safetyRating: z.number().min(1).max(6).nullable(),
    issueCounts: pilotFeedbackIssueCountsSchema,
    createdByName: z.string().min(1).max(200),
    createdAt: z.iso.datetime(),
});
export const hqPilotFeedbackCreateRequestSchema = z.object({
    collectedOn: z.iso.date(),
    respondentGroup: pilotFeedbackRespondentGroupSchema,
    source: pilotFeedbackSourceSchema,
    sampleSize: z.number().int().min(1).max(1000),
    usefulnessRating: z.number().min(1).max(6),
    understandingRating: z.number().min(1).max(6).nullable(),
    safetyRating: z.number().min(1).max(6).nullable(),
    issueCounts: pilotFeedbackIssueCountsSchema,
});
export const hqPilotFeedbackResponseSchema = z.object({
    feedback: z.array(hqPilotFeedbackSummarySchema),
    requestId: requestIdSchema,
});
export const hqPilotFeedbackMutationResponseSchema = z.object({
    feedback: hqPilotFeedbackSummarySchema,
    requestId: requestIdSchema,
});
export const pilotOperationalDrillTypeSchema = z.enum([
    "dependency_outage",
    "offline_mode",
    "lost_device",
    "device_replacement",
    "retention",
]);
export const pilotOperationalDrillOutcomeSchema = z.enum([
    "passed",
    "failed",
    "blocked",
]);
export const hqPilotOperationalDrillSchema = z.object({
    id: z.string().min(1).max(128),
    pilotId: z.string().min(1).max(128),
    schoolId: schoolIdSchema,
    drillType: pilotOperationalDrillTypeSchema,
    outcome: pilotOperationalDrillOutcomeSchema,
    performedOn: z.iso.date(),
    evidenceNote: z.string().min(2).max(1000),
    followUpAction: z.string().min(2).max(1000).nullable(),
    performedByName: z.string().min(1).max(200),
    performedAt: z.iso.datetime(),
});
export const hqPilotOperationalDrillCreateRequestSchema = z
    .object({
    drillType: pilotOperationalDrillTypeSchema,
    outcome: pilotOperationalDrillOutcomeSchema,
    performedOn: z.iso.date(),
    evidenceNote: z.string().trim().min(2).max(1000),
    followUpAction: z.string().trim().min(2).max(1000).nullable(),
})
    .superRefine((value, context) => {
    if (value.outcome !== "passed" && !value.followUpAction) {
        context.addIssue({
            code: "custom",
            message: "A follow-up action is required for a failed or blocked drill",
            path: ["followUpAction"],
        });
    }
});
export const hqPilotOperationalDrillsResponseSchema = z.object({
    drills: z.array(hqPilotOperationalDrillSchema),
    requiredTypes: z.array(pilotOperationalDrillTypeSchema).length(5),
    passedTypes: z.array(pilotOperationalDrillTypeSchema),
    missingTypes: z.array(pilotOperationalDrillTypeSchema),
    readyForCloseout: z.boolean(),
    requestId: requestIdSchema,
});
export const hqPilotOperationalDrillMutationResponseSchema = z.object({
    drill: hqPilotOperationalDrillSchema,
    requestId: requestIdSchema,
});
export const pilotCloseoutDecisionSchema = z.enum([
    "go",
    "conditional_go",
    "no_go",
]);
export const hqPilotCloseoutSchema = z.object({
    id: z.string().min(1).max(128),
    pilotId: z.string().min(1).max(128),
    schoolId: schoolIdSchema,
    decision: pilotCloseoutDecisionSchema,
    lessonCount: z.number().int().min(0),
    activeDayCount: z.number().int().min(0),
    ratedLessonCount: z.number().int().min(0),
    averageLessonRating: z.number().min(1).max(6).nullable(),
    feedbackSampleSize: z.number().int().min(0),
    averageUsefulnessRating: z.number().min(1).max(6).nullable(),
    averageUnderstandingRating: z.number().min(1).max(6).nullable(),
    averageSafetyRating: z.number().min(1).max(6).nullable(),
    teacherEscalationCount: z.number().int().min(0),
    safetyAlertCount: z.number().int().min(0),
    incidentCount: z.number().int().min(0),
    directCostMicrousd: z.number().int().min(0),
    nextActions: z.string().min(2).max(2000).nullable(),
    decidedByName: z.string().min(1).max(200),
    decidedAt: z.iso.datetime(),
});
export const hqPilotCloseoutCreateRequestSchema = z
    .object({
    decision: pilotCloseoutDecisionSchema,
    safetyReviewed: z.literal(true),
    learningReviewed: z.literal(true),
    operationsReviewed: z.literal(true),
    economicsReviewed: z.literal(true),
    schoolReadinessReviewed: z.literal(true),
    nextActions: z.string().trim().min(2).max(2000).nullable(),
})
    .superRefine((value, context) => {
    if (value.decision !== "go" && !value.nextActions) {
        context.addIssue({
            code: "custom",
            message: "Follow-up actions are required for this decision",
            path: ["nextActions"],
        });
    }
});
export const hqPilotCloseoutResponseSchema = z.object({
    closeout: hqPilotCloseoutSchema.nullable(),
    requestId: requestIdSchema,
});
export const hqPilotCloseoutMutationResponseSchema = z.object({
    closeout: hqPilotCloseoutSchema,
    requestId: requestIdSchema,
});
export const billingPlanSchema = z.object({
    code: schoolPlanCodeSchema,
    name: z.string().min(1).max(120),
    status: z.enum(["active", "archived"]),
    realtimeModel: openAiModelIdSchema,
    textVisionModel: openAiModelIdSchema,
    textVisionReasoningEffort: openAiReasoningEffortSchema,
    moderationEnabled: z.boolean(),
    contentRetentionDays: z.number().int().min(0).max(365),
    photoRetentionHours: z.number().int().min(0).max(168),
    baseMonthlyGrosz: z.number().int().min(0),
    perDeviceMonthlyGrosz: z.number().int().min(0),
    includedLessons: limitValuesSchema.shape.monthlyLessons,
    includedRealtimeMinutes: limitValuesSchema.shape.monthlyRealtimeMinutes,
    includedImageAnalyses: limitValuesSchema.shape.monthlyImageAnalyses,
    monthlyTextMessages: limitValuesSchema.shape.monthlyTextMessages,
    maxLessonMinutes: limitValuesSchema.shape.maxLessonMinutes,
    maxConcurrentLessons: limitValuesSchema.shape.maxConcurrentLessons,
    maxR2StorageBytes: limitValuesSchema.shape.maxR2StorageBytes,
    maxMaterials: limitValuesSchema.shape.maxMaterials,
    monthlyAiBudgetMicrousd: limitValuesSchema.shape.monthlyAiBudgetMicrousd,
    warningPercent: limitValuesSchema.shape.warningPercent,
    hardStop: limitValuesSchema.shape.hardStop,
    estimatedTextInputTokensPerMessage: z.number().int().min(1).max(1_000_000),
    estimatedTextOutputTokensPerMessage: z.number().int().min(1).max(1_000_000),
    estimatedImageInputTokensPerAnalysis: z.number().int().min(1).max(1_000_000),
    estimatedImageOutputTokensPerAnalysis: z.number().int().min(1).max(1_000_000),
    estimatedReasoningTokenPercent: z.number().int().min(0).max(1000),
    costReservePercent: z.number().int().min(0).max(500),
    updatedAt: z.iso.datetime(),
});
export const billingSubscriptionSchema = z.object({
    id: z.string().min(1).max(128),
    schoolId: schoolIdSchema,
    schoolName: z.string().min(1).max(240),
    planCode: schoolPlanCodeSchema,
    planName: z.string().min(1).max(120),
    status: z.enum(["trialing", "active", "past_due", "cancelled"]),
    contractedDevices: z.number().int().min(0).max(1000),
    monthlyValueGrosz: z.number().int().min(0),
    startsOn: z.iso.date(),
    renewsOn: z.iso.date().nullable(),
    updatedAt: z.iso.datetime(),
});
export const billingInvoiceStatusSchema = z.enum([
    "issued",
    "paid",
    "overdue",
    "cancelled",
]);
export const billingInvoiceSchema = z.object({
    id: z.string().min(1).max(128),
    schoolId: schoolIdSchema,
    schoolName: z.string().min(1).max(240),
    subscriptionId: z.string().min(1).max(128).nullable(),
    number: z.string().min(2).max(100),
    status: billingInvoiceStatusSchema,
    currency: z.literal("PLN"),
    netAmountGrosz: z.number().int().min(0),
    taxAmountGrosz: z.number().int().min(0),
    grossAmountGrosz: z.number().int().min(0),
    issuedOn: z.iso.date(),
    dueOn: z.iso.date(),
    paidAt: z.iso.datetime().nullable(),
    updatedAt: z.iso.datetime(),
});
export const hqBillingResponseSchema = z.object({
    summary: z.object({
        mrrGrosz: z.number().int().min(0),
        arrGrosz: z.number().int().min(0),
        activeSubscriptions: z.number().int().min(0),
        trialingSubscriptions: z.number().int().min(0),
        pastDueSubscriptions: z.number().int().min(0),
        directCost30dUsd: z.number().min(0),
        aiCostPerLesson30dUsd: z.number().min(0).nullable(),
        directCostPerDevice30dUsd: z.number().min(0).nullable(),
        operationalCost30dGrosz: z.number().int().min(0).default(0),
        fullyLoadedCostPerDevice30dGrosz: z.number().int().min(0).nullable(),
        operationalCostsConfirmed: z.boolean().default(false),
        grossMargin30dPercent: z.number().min(-100_000).max(100).nullable(),
        pilotConversionPercent: z.number().min(0).max(100).nullable(),
        churn30dPercent: z.number().min(0).max(100).nullable(),
        lessons30d: z.number().int().min(0),
        contractedActiveDevices: z.number().int().min(0),
        usdToPln: z.number().min(0.01).max(100),
    }),
    plans: z.array(billingPlanSchema),
    subscriptions: z.array(billingSubscriptionSchema),
    invoices: z.array(billingInvoiceSchema).default([]),
    generatedAt: z.iso.datetime(),
    requestId: requestIdSchema,
});
export const billingPlanUpdateRequestSchema = z.object({
    name: z.string().trim().min(1).max(120),
    status: z.enum(["active", "archived"]),
    realtimeModel: openAiModelIdSchema,
    textVisionModel: openAiModelIdSchema,
    textVisionReasoningEffort: openAiReasoningEffortSchema,
    moderationEnabled: z.boolean(),
    contentRetentionDays: z.number().int().min(0).max(365),
    photoRetentionHours: z.number().int().min(0).max(168),
    baseMonthlyGrosz: z.number().int().min(0).max(100_000_000),
    perDeviceMonthlyGrosz: z.number().int().min(0).max(100_000_000),
    includedLessons: limitValuesSchema.shape.monthlyLessons,
    includedRealtimeMinutes: limitValuesSchema.shape.monthlyRealtimeMinutes,
    includedImageAnalyses: limitValuesSchema.shape.monthlyImageAnalyses,
    monthlyTextMessages: limitValuesSchema.shape.monthlyTextMessages,
    maxLessonMinutes: limitValuesSchema.shape.maxLessonMinutes,
    maxConcurrentLessons: limitValuesSchema.shape.maxConcurrentLessons,
    maxR2StorageBytes: limitValuesSchema.shape.maxR2StorageBytes,
    maxMaterials: limitValuesSchema.shape.maxMaterials,
    monthlyAiBudgetMicrousd: limitValuesSchema.shape.monthlyAiBudgetMicrousd,
    warningPercent: limitValuesSchema.shape.warningPercent,
    hardStop: limitValuesSchema.shape.hardStop,
    estimatedTextInputTokensPerMessage: z.number().int().min(1).max(1_000_000),
    estimatedTextOutputTokensPerMessage: z.number().int().min(1).max(1_000_000),
    estimatedImageInputTokensPerAnalysis: z.number().int().min(1).max(1_000_000),
    estimatedImageOutputTokensPerAnalysis: z.number().int().min(1).max(1_000_000),
    estimatedReasoningTokenPercent: z.number().int().min(0).max(1000),
    costReservePercent: z.number().int().min(0).max(500),
});
export const billingPlanMutationResponseSchema = z.object({
    plan: billingPlanSchema,
    requestId: requestIdSchema,
});
export const billingInvoiceCreateRequestSchema = z
    .object({
    schoolId: schoolIdSchema,
    number: z.string().trim().min(2).max(100),
    netAmountGrosz: z.number().int().min(0).max(100_000_000),
    taxAmountGrosz: z.number().int().min(0).max(100_000_000),
    issuedOn: z.iso.date(),
    dueOn: z.iso.date(),
})
    .refine((value) => value.dueOn >= value.issuedOn, {
    message: "Invoice due date must not precede issue date",
    path: ["dueOn"],
});
export const billingInvoiceStatusUpdateRequestSchema = z.object({
    status: billingInvoiceStatusSchema,
});
export const billingInvoiceMutationResponseSchema = z.object({
    invoice: billingInvoiceSchema,
    requestId: requestIdSchema,
});
export const hqSearchQuerySchema = z.object({
    q: z.string().trim().min(2).max(100),
});
export const hqSearchResultSchema = z.object({
    type: z.enum(["school", "device", "lead", "incident"]),
    id: z.string().min(1).max(128),
    title: z.string().min(1).max(240),
    subtitle: z.string().max(500),
    path: z.string().startsWith("/").max(300),
    status: z.string().min(1).max(80),
});
export const hqSearchResponseSchema = z.object({
    results: z.array(hqSearchResultSchema).max(20),
    requestId: requestIdSchema,
});
const hqNotificationStaticPaths = new Set([
    "/",
    "/pilots",
    "/sales",
    "/settings",
    "/settings?tab=email",
    "/support",
]);
const schoolNotificationPaths = new Set([
    "/",
    "/curriculum",
    "/onboarding",
    "/reports",
    "/reports#teacher-requests",
    "/settings?tab=limits",
    "/stations",
    "/support",
]);
export const hqNotificationPathSchema = z
    .custom((value) => typeof value === "string" &&
    (hqNotificationStaticPaths.has(value) ||
        /^\/(?:fleet|schools)\/[A-Za-z0-9][A-Za-z0-9-]{2,127}$/.test(value)))
    .catch("/");
export const schoolNotificationPathSchema = z
    .custom((value) => typeof value === "string" &&
    schoolNotificationPaths.has(value))
    .catch("/");
const notificationBaseSchema = z.object({
    id: z.string().min(1).max(128),
    kind: z.enum([
        "lead",
        "pilot",
        "incident",
        "device",
        "quota",
        "escalation",
        "system",
    ]),
    severity: z.enum(["info", "success", "warning", "critical"]),
    title: z.string().min(1).max(240),
    body: z.string().min(1).max(1000),
    createdAt: z.iso.datetime(),
    readAt: z.iso.datetime().nullable(),
});
export const hqNotificationSchema = notificationBaseSchema.extend({
    path: hqNotificationPathSchema,
});
export const schoolNotificationSchema = notificationBaseSchema.extend({
    path: schoolNotificationPathSchema,
});
export const notificationSchema = z.union([
    hqNotificationSchema,
    schoolNotificationSchema,
]);
export const notificationsResponseSchema = z.object({
    notifications: z.array(notificationSchema).max(50),
    unreadCount: z.number().int().min(0),
    requestId: requestIdSchema,
});
export const hqNotificationsResponseSchema = notificationsResponseSchema.extend({
    notifications: z.array(hqNotificationSchema).max(50),
});
export const schoolNotificationsResponseSchema = notificationsResponseSchema.extend({
    notifications: z.array(schoolNotificationSchema).max(50),
});
export const notificationMutationResponseSchema = z.object({
    updatedCount: z.number().int().min(0),
    requestId: requestIdSchema,
});
export const hqAuditQuerySchema = z.object({
    days: z.coerce
        .number()
        .int()
        .pipe(z.union([z.literal(7), z.literal(30), z.literal(90)]))
        .default(30),
    action: z.string().trim().max(120).optional(),
    schoolId: schoolIdSchema.optional(),
    cursor: cursorSchema.optional(),
    limit: z.coerce.number().int().min(10).max(100).default(50),
});
export const hqAuditEntrySchema = z.object({
    id: z.string().min(1).max(128),
    actorType: z.enum(["user", "device", "system"]),
    actorId: z.string().max(128).nullable(),
    actorName: z.string().max(240).nullable(),
    actorEmail: z.email().nullable(),
    schoolId: schoolIdSchema.nullable(),
    schoolName: z.string().max(240).nullable(),
    action: z.string().min(1).max(120),
    targetType: z.string().max(120).nullable(),
    targetId: z.string().max(128).nullable(),
    requestId: z.string().max(128).nullable(),
    metadata: z.record(z.string(), z.unknown()),
    createdAt: z.iso.datetime(),
});
export const hqAuditResponseSchema = z.object({
    entries: z.array(hqAuditEntrySchema).max(100),
    actions: z.array(z.string().min(1).max(120)).max(100),
    periodDays: z.union([z.literal(7), z.literal(30), z.literal(90)]),
    page: cursorPageSchema,
    requestId: requestIdSchema,
});
export const hqSystemStatusResponseSchema = z.object({
    environment: z.string().min(1).max(40),
    release: z.string().min(1).max(80),
    security: z.object({
        authSecretConfigured: z.boolean(),
        secureHeadersEnabled: z.literal(true),
        corsAllowlistEnabled: z.literal(true),
        passwordRateLimitsEnabled: z.literal(true),
        hqRoleGuardEnabled: z.literal(true),
        edgeAccessStatus: z.literal("not_verifiable"),
    }),
    email: z.object({
        configured: z.boolean(),
        provider: z.literal("cloudflare_email_sending"),
        fallbackMode: z.enum(["delivery", "development_link"]),
        deliveriesLast24Hours: z.object({
            total: z.number().int().min(0),
            sent: z.number().int().min(0),
            failed: z.number().int().min(0),
            pending: z.number().int().min(0),
            skipped: z.number().int().min(0),
            stalePending: z.number().int().min(0),
        }),
    }),
    operations: z.object({
        devices: z.object({
            total: z.number().int().min(0),
            offline: z.number().int().min(0),
            maintenance: z.number().int().min(0),
            unmanaged: z.number().int().min(0),
        }),
        lessons: z.object({
            active: z.number().int().min(0),
            teacherEscalationsOpen: z.number().int().min(0),
        }),
        incidents: z.object({
            open: z.number().int().min(0),
            critical: z.number().int().min(0),
            slaBreached: z.number().int().min(0),
        }),
        queues: z.object({
            email: z.object({
                pending: z.number().int().min(0),
                failed: z.number().int().min(0),
                deadLetter: z.number().int().min(0),
            }),
            materials: z.object({
                pending: z.number().int().min(0),
                failed: z.number().int().min(0),
                deadLetter: z.number().int().min(0),
            }),
        }),
        ai: z.object({ enabled: z.boolean() }),
        measuredAt: z.iso.datetime(),
    }),
    requestId: requestIdSchema,
});
export const stationPairingRequestSchema = z.object({
    code: stationPairingCodeSchema,
    token: stationDeviceTokenSchema,
    device: z.object({
        appVersion: z.string().trim().min(1).max(40),
        platform: devicePlatformSchema,
        osVersion: z.string().trim().min(1).max(40),
        management: managedDeviceIdentitySchema.optional(),
    }),
});
export const schoolDeviceEnrollmentCreateRequestSchema = z.object({
    name: z.string().trim().min(2).max(200),
    location: z.string().trim().min(1).max(200).nullable(),
});
export const schoolDeviceEnrollmentResponseSchema = z.object({
    enrollmentId: z.string().min(1).max(64),
    deviceId: z.string().min(1).max(64),
    code: stationPairingCodeSchema,
    qrPayload: z.string().url().max(512),
    stationName: z.string().min(2).max(200),
    location: z.string().min(1).max(200).nullable(),
    expiresAt: z.iso.datetime(),
    requestId: requestIdSchema,
});
export const stationPairingResponseSchema = z.object({
    credential: z.object({
        token: stationDeviceTokenSchema,
    }),
    station: z.object({
        deviceId: z.string().min(1).max(64),
        schoolId: schoolIdSchema,
        schoolName: z.string().min(1).max(200),
        name: z.string().min(1).max(200),
        location: z.string().min(1).max(200).nullable(),
    }),
    heartbeat: z.object({
        intervalSeconds: z.number().int().min(60).max(900),
    }),
    requestId: requestIdSchema,
});
export const stationCredentialRotateRequestSchema = z.object({
    nextToken: stationDeviceTokenSchema,
});
export const stationCredentialRotateResponseSchema = z.object({
    credential: z.object({
        token: stationDeviceTokenSchema,
    }),
    requestId: requestIdSchema,
});
//# sourceMappingURL=index.js.map